import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Save, PieChart, Bell, Languages, Target, Info, Tag, ExternalLink, Server, Lock, ChevronDown, CheckCircle2, Zap as ZapIcon, Sparkles, Cpu, ChevronLeft, ChevronRight, X, Calendar, Bot, Clock, Globe, RotateCcw, Coins, ShieldCheck, AlertTriangle, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { fetchUserProfile, updateUserProfile, fetchUserAIConfig, updateUserAIConfig, fetchUserApiKeys, updateUserApiKey, fetchUserTradingConfig, updateUserTradingConfig, testUserWebhook, fetchPortfolioHwm, resetPortfolioHwm, PortfolioHwmStatus } from '../services/api';
import { Holding, MembershipTier, canAccessTier } from '../types';
import { ApiKeys } from '../hooks/useSettings';
import { useTierGate } from '../hooks/useTierGate';
import { useCollapsibleState } from '../hooks/useCollapsibleState';
import { TierBadge } from './TierGate';
import { useTerminology } from '../hooks/useTerminology';
import { useLiveTradingMode } from '../hooks/useLiveTradingMode';
import { SubscriptionCancelModal } from './SubscriptionCancelModal';
import { DeviceManagement } from './DeviceManagement';
import { CollapsibleSection } from './common/CollapsibleSection';
import packageJson from '../../package.json';
import { formatProductVersion } from '../utils/version';
const formatNumberWithCommas = (val: number | string) => {
  if (val === undefined || val === null || val === '') return '';
  const clean = String(val).replace(/[^0-9]/g, '');
  if (!clean) return '';
  return Number(clean).toLocaleString();
};

// ─── Model Catalog ──────────────────────────────────────────────────────────
const MODEL_CATALOG: Record<string, { id: string; label: string; tier: 'flagship' | 'fast' | 'reasoning' }[]> = {
  gemini: [
    { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', tier: 'fast' },
    { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', tier: 'flagship' },
    { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (Stable)', tier: 'fast' },
  ],
  openai: [
    { id: 'gpt-4o', label: 'GPT-4o', tier: 'flagship' },
    { id: 'gpt-4o-mini', label: 'GPT-4o mini', tier: 'fast' },
    { id: 'o1-preview', label: 'o1 Preview', tier: 'reasoning' },
    { id: 'gpt-4-turbo', label: 'GPT-4 Turbo', tier: 'flagship' },
  ],
  anthropic: [
    { id: 'claude-3-5-sonnet-20241022', label: 'Claude 3.5 Sonnet', tier: 'flagship' },
    { id: 'claude-3-opus-20240229', label: 'Claude 3 Opus', tier: 'flagship' },
    { id: 'claude-3-haiku-20240307', label: 'Claude 3 Haiku', tier: 'fast' },
  ],
};

const TIER_COLORS: Record<string, string> = {
  flagship: 'text-violet-400',
  fast: 'text-emerald-400',
  reasoning: 'text-amber-400',
};

const PROVIDER_COLORS: Record<string, string> = {
  gemini: 'text-blue-400',
  openai: 'text-emerald-400',
  anthropic: 'text-orange-400',
  'Ollama Cloud': 'text-teal-400',
};

// ─── ModelSelect Component ───────────────────────────────────────────────────
interface ModelSelectProps {
  value: string;
  availableProviders: string[];
  onChange: (modelId: string) => void;
  label: string;
  canUseAllModels?: boolean;
}

function ModelSelect({ value, availableProviders, onChange, label, canUseAllModels = false }: ModelSelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const allModels = Object.entries(MODEL_CATALOG).flatMap(([provider, models]) =>
    models.map(m => ({ ...m, provider }))
  );
  const selected = allModels.find(m => m.id === value);

  return (
    <div ref={ref} className="relative text-left">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between bg-black/50 border border-white/10 rounded-xl px-3 py-2 text-sm focus:border-emerald-500 outline-none transition-all font-mono hover:border-white/20"
      >
        <span className="truncate">
          {selected ? (
            <span className="flex items-center gap-2">
              <span className={`text-[10px] font-bold uppercase ${PROVIDER_COLORS[selected.provider] || 'text-white/40'}`}>{selected.provider}</span>
              <span className="text-white">{selected.label}</span>
            </span>
          ) : (
            <span className="text-white/40">{label}</span>
          )}
        </span>
        <ChevronDown className="w-4 h-4 text-white/40 ml-2" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 5 }}
            className="absolute z-50 mt-1 w-full bg-zinc-950 border border-white/10 rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto"
          >
            <div className="p-1.5 space-y-1">
              {allModels.map((m) => {
                const isProviderAvailable = availableProviders.includes(m.provider);
                const disabled = !canUseAllModels && !isProviderAvailable;
                return (
                  <button
                    key={m.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      onChange(m.id);
                      setOpen(false);
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left rounded-lg text-xs font-mono transition-all ${disabled
                      ? 'opacity-30 cursor-not-allowed hover:bg-transparent'
                      : 'hover:bg-white/5 text-white/80 hover:text-white'
                      }`}
                  >
                    <span className="flex items-center gap-1.5">
                      <span className={`text-[9px] font-black uppercase ${PROVIDER_COLORS[m.provider]}`}>{m.provider}</span>
                      <span>{m.label}</span>
                    </span>
                    {disabled && <Lock className="w-3 h-3 text-white/30" />}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export interface SettingsViewProps {
  user: any;
  userProfile: any;
  apiKeys: ApiKeys;
  onSaveApiKeys: (keys: ApiKeys) => Promise<void> | void;
  holdings: Holding[];
  onUpdateTargetWeight: (symbol: string, weight: number) => Promise<void>;
  onUpdateProfile: (updates: any) => void;
  currencyPreference?: 'USD' | 'KRW';
  onCurrencyPreferenceChange?: (currency: 'USD' | 'KRW') => void;
  onUpgradeRequest?: (tier: any, featureName: any, useExistingCard?: any, billingCycle?: any) => void;
  onRefreshProfile?: () => Promise<void>;
  isAiPersonalizationEnabled?: boolean;
  featureDisabledMode?: 'NOTICE' | 'HIDE';
}

export function SettingsView({
  user,
  userProfile,
  apiKeys,
  onSaveApiKeys,
  holdings,
  onUpdateTargetWeight,
  onUpdateProfile,
  currencyPreference = 'USD',
  onCurrencyPreferenceChange,
  onUpgradeRequest,
  onRefreshProfile,
  isAiPersonalizationEnabled = true,
  featureDisabledMode = 'NOTICE'
}: SettingsViewProps) {
  // 실거래(Private Terminal) 모드 단일 진실원 — 실거래 호스트 + 백엔드 권한 플래그(canAccessLiveTrading) 기반
  const isLiveMode = useLiveTradingMode(userProfile);
  const { getFeatureName } = useTerminology(userProfile?.uiLanguage, isLiveMode);
  const [localKeys, setLocalKeys] = useState<ApiKeys>(apiKeys);
  const [saved, setSaved] = useState(false);
  const [cancelModalConfig, setCancelModalConfig] = useState<{
    tier: MembershipTier | 'AI_PERSONALIZATION_ADDON' | 'CHAT_PREDICTION_ADDON' | 'API_ACCESS_ADDON' | 'OPTIONS_ANALYTICS_ADDON' | 'addon_agent_standard' | 'addon_agent_pro' | 'addon_agent_vip';
    subId?: string;
  } | null>(null);

  const [useExistingCardAuto, setUseExistingCardAuto] = useState(true);
  const [useExistingCardPersonal, setUseExistingCardPersonal] = useState(true);
  const [useExistingCardApi, setUseExistingCardApi] = useState(true);

  const { tier, limits, hasAutoTradingAccess: gateAutoTradingAccess } = useTierGate(userProfile || null);

  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [vixThreshold, setVixThreshold] = useState<number>(30.0);
  const [mddThreshold, setMddThreshold] = useState<number>(-5.0);
  const [bondYieldThreshold, setBondYieldThreshold] = useState<number>(0.10);
  const [usdKrwResistance, setUsdKrwResistance] = useState<number>(1400.0);
  const [usdKrwSupport, setUsdKrwSupport] = useState<number>(1300.0);
  // 5-Domain Alert Policies States
  const [stopLossThreshold, setStopLossThreshold] = useState<number>(-8.0);
  const [concentrationThreshold, setConcentrationThreshold] = useState<number>(35.0);
  const [oilSpikeThreshold, setOilSpikeThreshold] = useState<number>(5.0);
  const [vixRecoveryAlertEnabled, setVixRecoveryAlertEnabled] = useState<boolean>(true);
  const [executionAlertEnabled, setExecutionAlertEnabled] = useState<boolean>(true);
  const [billingAlertEnabled, setBillingAlertEnabled] = useState<boolean>(true);
  const [isSavingAlerts, setIsSavingAlerts] = useState<boolean>(false);
  const [uiLanguage, setUiLanguage] = useState<'ko' | 'en'>('ko');
  const [analysisLanguage, setAnalysisLanguage] = useState<'ko' | 'en'>('ko');

  // Portfolio HWM (High-Water Mark) Anchor States
  const [hwmStatus, setHwmStatus] = useState<PortfolioHwmStatus | null>(null);
  const [isResettingHwm, setIsResettingHwm] = useState<boolean>(false);
  const [hwmFeedback, setHwmFeedback] = useState<string | null>(null);

  const loadHwm = useCallback(async () => {
    try {
      const status = await fetchPortfolioHwm();
      if (status) setHwmStatus(status);
    } catch (e) {
      console.warn('Failed to load HWM status:', e);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadHwm();
    }
  }, [user, loadHwm]);

  const handleResetHwm = async (customVal?: number) => {
    setIsResettingHwm(true);
    setHwmFeedback(null);
    try {
      const res = await resetPortfolioHwm(customVal);
      if (res.success) {
        setHwmFeedback(res.message || (uiLanguage === 'ko' ? '포트폴리오 고점(HWM) 기준이 성공적으로 재설정되었습니다.' : 'Portfolio HWM baseline has been reset successfully.'));
        await loadHwm();
        window.dispatchEvent(new CustomEvent('app:refresh-notifications'));
      }
    } catch (err: any) {
      setHwmFeedback(err.message || 'HWM Reset Failed');
    } finally {
      setIsResettingHwm(false);
      setTimeout(() => setHwmFeedback(null), 5000);
    }
  };

  // AI Quant Data Feed & Webhook Pro Settings States
  const [autoTradeEnabled, setAutoTradeEnabled] = useState<boolean>(false);
  const [autoTradeBroker, setAutoTradeBroker] = useState<'smart_routing' | 'kis' | 'ls' | 'kiwoom' | 'toss'>('smart_routing');
  const [autoTradeBudgetUS, setAutoTradeBudgetUS] = useState<number>(10000);
  const [autoTradeDailyOrderLimitUS, setAutoTradeDailyOrderLimitUS] = useState<number>(50000);
  const [autoTradeBudgetKR, setAutoTradeBudgetKR] = useState<number>(10000000);
  const [autoTradeDailyOrderLimitKR, setAutoTradeDailyOrderLimitKR] = useState<number>(50000000);

  const [autoTradeMaxWeight, setAutoTradeMaxWeight] = useState<number>(25);
  const [autoTradeStopLoss, setAutoTradeStopLoss] = useState<number>(8);
  const [autoTradeMddBreaker, setAutoTradeMddBreaker] = useState<number>(15);
  const [isSavingTradingConfig, setIsSavingTradingConfig] = useState<boolean>(false);
  const [agentHitlPreference, setAgentHitlPreference] = useState<'hitl' | 'auto'>(userProfile?.agentHitlPreference || 'hitl');
  const [targetMarkets, setTargetMarkets] = useState<('KR' | 'US')[]>(['US', 'KR']);
  const [scheduleMode, setScheduleMode] = useState<'MARKET_HOURS_ONLY' | 'ALL_DAY'>('MARKET_HOURS_ONLY');
  const [usExecutionSession, setUsExecutionSession] = useState<'REGULAR_ONLY' | 'PRE_MARKET' | 'ALLOW_DAY_MARKET'>('REGULAR_ONLY');
  const [maxSpreadPct, setMaxSpreadPct] = useState<number>(0.8);
  const [useIntegratedMargin, setUseIntegratedMargin] = useState<boolean>(true);
  const [paperExecutionMode, setPaperExecutionMode] = useState<'VIRTUAL_SANDBOX' | 'BROKER_PAPER'>('VIRTUAL_SANDBOX');
  const [virtualSeedKRW, setVirtualSeedKRW] = useState<number>(100000000);
  const [virtualSeedUSD, setVirtualSeedUSD] = useState<number>(100000);
  const [paperBroker, setPaperBroker] = useState<'smart_routing' | 'kis' | 'ls' | 'kiwoom' | 'toss'>('smart_routing');
  const [paperHitlPreference, setPaperHitlPreference] = useState<'hitl' | 'auto'>('hitl');
  const [paperScheduleMode, setPaperScheduleMode] = useState<'MARKET_HOURS_ONLY' | 'ALL_DAY'>('MARKET_HOURS_ONLY');
  const [paperMaxWeight, setPaperMaxWeight] = useState<number>(25);
  const [paperStopLoss, setPaperStopLoss] = useState<number>(8);
  const [paperMddBreaker, setPaperMddBreaker] = useState<number>(15);
  const [paperDailyOrderLimitKR, setPaperDailyOrderLimitKR] = useState<number>(50000000);
  const [paperDailyOrderLimitUS, setPaperDailyOrderLimitUS] = useState<number>(50000);

  // Webhook Pro Specific States
  const [alertChannelType, setAlertChannelType] = useState<'DISCORD' | 'SLACK' | 'TELEGRAM'>('DISCORD');
  const [webhookUrl, setWebhookUrl] = useState<string>('');
  const [telegramChatId, setTelegramChatId] = useState<string>('');
  const [isTestingWebhook, setIsTestingWebhook] = useState<boolean>(false);
  const [webhookTestMessage, setWebhookTestMessage] = useState<{ text: string; isError: boolean } | null>(null);

  useEffect(() => {
    if (userProfile?.agentHitlPreference) {
      setAgentHitlPreference(userProfile.agentHitlPreference);
    }
  }, [userProfile?.agentHitlPreference]);

  // Active Stock Market Mode selected on Dashboard ('US' or 'KR')
  const activeMarketMode = (userProfile?.targetMarket as 'US' | 'KR') || (currencyPreference === 'KRW' ? 'KR' : 'US');
  const autoTradeCurrency = activeMarketMode === 'KR' ? 'KRW' : 'USD';

  // Fetch Auto-Trading Configuration on Mount
  useEffect(() => {
    if (!user) return;
    const loadTradingConfig = async () => {
      try {
        const config = await fetchUserTradingConfig();
        if (config) {
          // 1. 실거래 허용 모드 (스마트 주문) 설정 로드
          setAutoTradeEnabled(config.enabled);
          const sanitizedLiveBroker = (config.broker as 'smart_routing' | 'kis' | 'ls' | 'kiwoom' | 'toss') || 'smart_routing';
          setAutoTradeBroker(sanitizedLiveBroker);
          setAutoTradeBudgetUS(config.budget_us ?? config.budget ?? 10000);
          setAutoTradeDailyOrderLimitUS(config.daily_order_limit_us ?? config.daily_order_limit ?? 50000);
          setAutoTradeBudgetKR(config.budget_kr ?? 10000000);
          setAutoTradeDailyOrderLimitKR(config.daily_order_limit_kr ?? 50000000);
          setAutoTradeMaxWeight(config.max_weight ?? 25);
          setAutoTradeStopLoss(config.stop_loss ?? 8);
          setAutoTradeMddBreaker(config.mdd_breaker ?? 15);
          setAgentHitlPreference(config.order_execution_mode || 'hitl');
          if (config.schedule_mode) {
            setScheduleMode(config.schedule_mode);
          }

          // 2. 모의투자 모드 (시그널 레이더) 독립 설정 로드
          setPaperBroker(config.paper_broker || 'smart_routing');
          setPaperHitlPreference(config.paper_order_execution_mode || 'hitl');
          setPaperScheduleMode(config.paper_schedule_mode || 'MARKET_HOURS_ONLY');
          setPaperMaxWeight(config.paper_max_weight ?? 25);
          setPaperStopLoss(config.paper_stop_loss ?? 8);
          setPaperMddBreaker(config.paper_mdd_breaker ?? 15);
          setPaperDailyOrderLimitKR(config.paper_daily_order_limit_kr ?? 50000000);
          setPaperDailyOrderLimitUS(config.paper_daily_order_limit_us ?? 50000);
          if (config.paper_execution_mode) {
            setPaperExecutionMode(config.paper_execution_mode);
          }
          if (config.virtual_seed_krw !== undefined) {
            setVirtualSeedKRW(config.virtual_seed_krw);
          } else if (config.paper_seed_krw !== undefined) {
            setVirtualSeedKRW(config.paper_seed_krw);
          }
          if (config.virtual_seed_usd !== undefined) {
            setVirtualSeedUSD(config.virtual_seed_usd);
          } else if (config.paper_seed_usd !== undefined) {
            setVirtualSeedUSD(config.paper_seed_usd);
          }

          // 3. 공통 알림 및 세션 채널
          if (config.alert_channel_type) {
            setAlertChannelType(config.alert_channel_type);
          }
          if (config.webhook_url) {
            setWebhookUrl(config.webhook_url);
          }
          if (config.telegram_chat_id) {
            setTelegramChatId(config.telegram_chat_id);
          }
          if (config.target_markets && Array.isArray(config.target_markets)) {
            setTargetMarkets(config.target_markets);
          }
          if (config.us_execution_session) {
            setUsExecutionSession(config.us_execution_session);
          }
          if (config.max_spread_pct !== undefined && config.max_spread_pct !== null) {
            setMaxSpreadPct(Number(config.max_spread_pct));
          }
          if (config.use_integrated_margin !== undefined) {
            setUseIntegratedMargin(config.use_integrated_margin !== false);
          }
        }
      } catch (err) {
        console.error("Failed to load trading configuration from server", err);
      }
    };
    loadTradingConfig();
  }, [user]);

  const handleTestWebhookClick = async () => {
    if (alertChannelType !== 'TELEGRAM' && !webhookUrl.trim()) {
      alert(uiLanguage === 'ko' ? 'Webhook URL을 입력해주세요.' : 'Please enter Webhook URL.');
      return;
    }
    if (alertChannelType === 'TELEGRAM' && !telegramChatId.trim()) {
      alert(uiLanguage === 'ko' ? 'Telegram Chat ID를 입력해주세요.' : 'Please enter Telegram Chat ID.');
      return;
    }
    setIsTestingWebhook(true);
    setWebhookTestMessage(null);
    try {
      const res = await testUserWebhook({
        channelType: alertChannelType,
        webhookUrl: webhookUrl.trim(),
        telegramChatId: telegramChatId.trim()
      });
      setWebhookTestMessage({
        text: uiLanguage === 'ko' ? (res.message || '✅ 테스트 웹훅 메시지가 성공적으로 발송되었습니다.') : (res.message || '✅ Test webhook sent successfully.'),
        isError: false
      });
    } catch (err: any) {
      setWebhookTestMessage({
        text: err.message || (uiLanguage === 'ko' ? '❌ 웹훅 발송에 실패했습니다.' : '❌ Webhook dispatch failed.'),
        isError: true
      });
    } finally {
      setIsTestingWebhook(false);
    }
  };

  const handleSaveAutoTrade = async () => {
    setIsSavingTradingConfig(true);
    try {
      const activeBudget = activeMarketMode === 'KR' ? autoTradeBudgetKR : autoTradeBudgetUS;
      const activeLimit = activeMarketMode === 'KR' ? autoTradeDailyOrderLimitKR : autoTradeDailyOrderLimitUS;

      await updateUserTradingConfig({
        enabled: autoTradeEnabled,
        broker: autoTradeBroker,
        budget: activeBudget,
        max_weight: autoTradeMaxWeight,
        stop_loss: autoTradeStopLoss,
        mdd_breaker: autoTradeMddBreaker,
        daily_order_limit: activeLimit,
        budget_us: autoTradeBudgetUS,
        daily_order_limit_us: autoTradeDailyOrderLimitUS,
        budget_kr: autoTradeBudgetKR,
        daily_order_limit_kr: autoTradeDailyOrderLimitKR,
        is_paper_trading: !isLiveMode,
        order_execution_mode: agentHitlPreference,
        target_markets: targetMarkets,
        schedule_mode: scheduleMode,
        use_integrated_margin: useIntegratedMargin,
        us_execution_session: usExecutionSession,
        max_spread_pct: maxSpreadPct,
        alert_channel_type: alertChannelType,
        webhook_url: webhookUrl.trim(),
        telegram_chat_id: telegramChatId.trim(),
        simulation_mode: agentHitlPreference,
        paper_execution_mode: paperExecutionMode,
        virtual_seed_krw: virtualSeedKRW,
        virtual_seed_usd: virtualSeedUSD,
        paper_seed_krw: virtualSeedKRW,
        paper_seed_usd: virtualSeedUSD,
        paper_schedule_mode: paperScheduleMode,
        paper_order_execution_mode: paperHitlPreference,
        paper_max_weight: paperMaxWeight,
        paper_stop_loss: paperStopLoss,
        paper_mdd_breaker: paperMddBreaker,
        paper_daily_order_limit_kr: paperDailyOrderLimitKR,
        paper_daily_order_limit_us: paperDailyOrderLimitUS,
        paper_broker: paperBroker
      });
      
      // agentHitlPreference 서버 프로필 및 메모리 동시 업데이트 (2중 저장 보장 - 실거래 실행 모드는 paper 탭 상태와 분리 보호)
      try {
        await updateUserProfile({ agentHitlPreference });
      } catch (profileErr) {
        console.warn('Profile sync warning:', profileErr);
      }
      if (onUpdateProfile) {
        onUpdateProfile({ agentHitlPreference });
      }

      // Sync local storage for fallback/compatibility if needed
      localStorage.setItem(`mmo_${user?.uid || 'guest'}_autotrade`, JSON.stringify({
        enabled: autoTradeEnabled,
        budget_us: autoTradeBudgetUS,
        daily_order_limit_us: autoTradeDailyOrderLimitUS,
        budget_kr: autoTradeBudgetKR,
        daily_order_limit_kr: autoTradeDailyOrderLimitKR,
        budget: activeBudget,
        dailyOrderLimit: activeLimit,
        maxWeight: autoTradeMaxWeight,
        stopLoss: autoTradeStopLoss,
        alertChannelType,
        webhookUrl: webhookUrl.trim(),
        telegramChatId: telegramChatId.trim(),
        simulationMode: agentHitlPreference,
        paperExecutionMode,
        virtualSeedKRW,
        virtualSeedUSD
      }));
      window.dispatchEvent(new CustomEvent('trading-config:updated', { detail: { paperExecutionMode } }));
      window.dispatchEvent(new CustomEvent('app:refresh'));
      alert(isLiveMode
        ? (uiLanguage === 'ko' ? '스마트 주문 설정이 성공적으로 저장되었습니다.' : 'Smart Order settings saved successfully.')
        : (uiLanguage === 'ko' ? '시그널 레이더 설정이 성공적으로 저장되었습니다.' : 'Signal Radar settings saved successfully.'));
    } catch (err: any) {
      console.error(err);
      alert(uiLanguage === 'ko' ? `설정 저장 실패: ${err.message}` : `Failed to save configuration: ${err.message}`);
    } finally {
      setIsSavingTradingConfig(false);
    }
  };

  // Dashboard Font Size
  const FONT_SIZE_OPTIONS = [
    { value: '12px', label: uiLanguage === 'ko' ? '매우 작게' : 'XS' },
    { value: '13px', label: uiLanguage === 'ko' ? '작게' : 'S' },
    { value: '14px', label: uiLanguage === 'ko' ? '보통' : 'M' },
    { value: '15px', label: uiLanguage === 'ko' ? '크게' : 'L' },
    { value: '16px', label: uiLanguage === 'ko' ? '매우 크게' : 'XL' },
  ];
  const [dashboardFontSize, setDashboardFontSize] = useState<string>(() => {
    return localStorage.getItem('mmo_dashboard_font_size') || '14px';
  });

  const handleFontSizeChange = (size: string) => {
    setDashboardFontSize(size);
    localStorage.setItem('mmo_dashboard_font_size', size);
    const existingEl = document.getElementById('mmo-sec-style-font');
    if (existingEl) existingEl.remove();
    const styleEl = document.getElementById('mmo-user-style-font') || document.createElement('style');
    styleEl.id = 'mmo-user-style-font';
    styleEl.innerHTML = `html { font-size: ${size} !important; }`;
    document.head.appendChild(styleEl);
  };

  useEffect(() => {
    const saved = localStorage.getItem('mmo_dashboard_font_size');
    if (saved) handleFontSizeChange(saved);
   
  }, []);

  const [targetGoalAmountUS, setTargetGoalAmountUS] = useState<number>(0);
  const [targetGoalAmountKR, setTargetGoalAmountKR] = useState<number>(0);
  const [investorType, setInvestorType] = useState<number>(3);
  const [investorExperienceTier, setInvestorExperienceTier] = useState<number>(1);
  const [investmentYears, setInvestmentYears] = useState<number>(0);
  const [activeSandboxMode, setActiveSandboxMode] = useState<boolean>(true);
  const normalizedUserTier = ((tier as any) === 'FREE' ? 'TIER1' : (tier as any) === 'PRO' ? 'TIER3' : (tier as any) === 'PREMIUM' ? 'TIER5' : tier) || 'TIER1';
  const [activeDetailTier, setActiveDetailTier] = useState<MembershipTier>(normalizedUserTier as any);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [isSpecModalOpen, setIsSpecModalOpen] = useState(false);

  const [rebalanceInterval, setRebalanceInterval] = useState<string>('monthly');
  const [isSavingGoal, setIsSavingGoal] = useState<boolean>(false);
  const [isSavingRebalance, setIsSavingRebalance] = useState<boolean>(false);
  const [productsConfig, setProductsConfig] = useState<any>(null);

  // Collapsible Sections State (persisted locally and via server profile)
  const {
    state: collapsedSections,
    toggleSection
  } = useCollapsibleState({
    namespace: 'settings',
    defaultState: {},
    userProfile,
    onUpdateProfile
  });

  useEffect(() => {
    fetch('/api/payment/products')
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('fail');
      })
      .then((data: any) => {
        if (data && data.products) {
          setProductsConfig(data.products);
        }
      })
      .catch(err => console.error("Failed to load products list:", err));
  }, []);

  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [userAiConfigs, setUserAiConfigs] = useState<any[]>([]);
  const [userApiKeys, setUserApiKeys] = useState<any[]>([]);
  const [isSavingAiConfig, setIsSavingAiConfig] = useState<string | null>(null);
  const [isSavingApiKey, setIsSavingApiKey] = useState<string | null>(null);
  const [verifiedProviders, setVerifiedProviders] = useState<string[]>([]);
  const [aiPersonalizationEnabled, setAiPersonalizationEnabled] = useState<boolean>(false);

  const [activeAddons, setActiveAddons] = useState<any[]>([]);

  useEffect(() => {
    fetch('/api/addon-config')
      .then(res => res.json())
      .then((data: any) => {
        if (Array.isArray(data)) {
          setActiveAddons(data);
        }
      })
      .catch(err => console.error("Failed to load active addons in SettingsView:", err));
  }, []);

  const checkAddonAccess = useCallback((addonId: string) => {
    // 1. Check if the addon has a minimum tier requirement
    const addonInfo = productsConfig?.addons?.find(a => a.id === addonId);
    if (addonInfo && addonInfo.minTier && addonInfo.minTier !== 'TIER1') {
      const userTier = userProfile?.membershipTier || 'TIER1';
      if (!canAccessTier(userTier, addonInfo.minTier as MembershipTier)) {
        return false;
      }
    }

    const userTier = (userProfile?.membershipTier as any) || 'TIER1';
    const normalizedUserTier: MembershipTier = userTier === 'FREE' ? 'TIER1' : userTier === 'PRO' ? 'TIER3' : userTier === 'PREMIUM' ? 'TIER5' : userTier;

    // 2. Check if the addon is free in the current tier
    let freeAddOns: string[] = [];
    if (productsConfig && productsConfig[normalizedUserTier]) {
      freeAddOns = productsConfig[normalizedUserTier].freeAddOns || [];
    }

    const isFreeInTier = freeAddOns.includes(addonId) || 
                         (addonId === 'CHAT_PREDICTION_ADDON' && freeAddOns.includes('CHAT_PLUS_ADDON')) ||
                         (addonId === 'API_ACCESS_ADDON' && freeAddOns.includes('DEVELOPER_API_ADDON'));

    if (isFreeInTier) {
      const tierExpired = userProfile?.tierExpiresAt && new Date(userProfile.tierExpiresAt) < new Date();
      if (!tierExpired) return true;
    }

    // 3. Check activeAddons array from backend (Signal Webhook / Smart Order 계층 지원: VIP > Pro > Standard)
    const matchingAddonIds = [addonId];
    if (addonId === 'addon_agent_pro') {
      matchingAddonIds.push('addon_agent_vip');
    } else if (addonId === 'addon_agent_standard') {
      matchingAddonIds.push('addon_agent_vip', 'addon_agent_pro');
    } else if (addonId === 'addon_smart_order_pro') {
      matchingAddonIds.push('addon_smart_order_vip');
    } else if (addonId === 'addon_smart_order_standard') {
      matchingAddonIds.push('addon_smart_order_vip', 'addon_smart_order_pro');
    }

    const activeAddon = userProfile?.activeAddons?.find((a: any) => matchingAddonIds.includes(a.id));
    if (!activeAddon) return false;
    
    if (activeAddon.expiresAt) {
      return new Date(activeAddon.expiresAt) > new Date();
    }
    
    return true;
  }, [userProfile, productsConfig]);

  useEffect(() => {
    if (userProfile) {
      setNotificationsEnabled(!!userProfile.notificationsEnabled);
      setVixThreshold(userProfile.vixThreshold !== undefined ? userProfile.vixThreshold : 30.0);
      setMddThreshold(userProfile.mddThreshold !== undefined ? userProfile.mddThreshold : -5.0);
      setBondYieldThreshold(userProfile.bondYieldThreshold !== undefined ? userProfile.bondYieldThreshold : 0.10);
      setUsdKrwResistance(userProfile.usdKrwResistance !== undefined ? userProfile.usdKrwResistance : 1400.0);
      setUsdKrwSupport(userProfile.usdKrwSupport !== undefined ? userProfile.usdKrwSupport : 1300.0);
      setStopLossThreshold(userProfile.stopLossThreshold !== undefined ? userProfile.stopLossThreshold : -8.0);
      setConcentrationThreshold(userProfile.concentrationThreshold !== undefined ? userProfile.concentrationThreshold : 35.0);
      setOilSpikeThreshold(userProfile.oilSpikeThreshold !== undefined ? userProfile.oilSpikeThreshold : 5.0);
      setVixRecoveryAlertEnabled(userProfile.vixRecoveryAlertEnabled !== undefined ? !!userProfile.vixRecoveryAlertEnabled : true);
      setExecutionAlertEnabled(userProfile.executionAlertEnabled !== undefined ? !!userProfile.executionAlertEnabled : true);
      setBillingAlertEnabled(userProfile.billingAlertEnabled !== undefined ? !!userProfile.billingAlertEnabled : true);
      setUiLanguage(userProfile.uiLanguage || userProfile.language || 'ko');
      setAnalysisLanguage(userProfile.analysisLanguage || userProfile.language || 'ko');
      setTargetGoalAmountUS(userProfile.targetGoalAmountUS || 0);
      setTargetGoalAmountKR(userProfile.targetGoalAmountKR || 0);
      setInvestorType(userProfile.investorType || 3);
      setInvestorExperienceTier(userProfile.investorExperienceTier !== undefined ? userProfile.investorExperienceTier : 1);
      setInvestmentYears(userProfile.investmentYears !== undefined ? userProfile.investmentYears : 0);
      setActiveSandboxMode(userProfile.activeSandboxMode !== undefined ? userProfile.activeSandboxMode : true);
      const normalizedDetail = ((userProfile.membershipTier as any) === 'FREE' ? 'TIER1' : (userProfile.membershipTier as any) === 'PRO' ? 'TIER3' : (userProfile.membershipTier as any) === 'PREMIUM' ? 'TIER5' : userProfile.membershipTier) || 'TIER1';
      setActiveDetailTier(normalizedDetail as any);
      setRebalanceInterval(userProfile.rebalanceInterval || 'monthly');
      setIsLoadingProfile(false);
    }
  }, [userProfile]);

  useEffect(() => {
    if (user) {
      fetchUserAIConfig().then(configs => {
        const configArray = Array.isArray(configs) ? configs : Object.entries(configs).map(([cat, val]: any) => ({ category: cat, ...val }));
        const toggleConfig = configArray.find((c: any) => c.category === '_personalization_toggle');
        if (toggleConfig) {
          setAiPersonalizationEnabled(toggleConfig.model_id === 'on');
        } else {
          setAiPersonalizationEnabled(false);
        }
        setUserAiConfigs(configArray.filter((c: any) => c.category !== '_personalization_toggle'));
      });

      fetchUserApiKeys().then(keys => {
        setUserApiKeys(keys);
        const providers = keys.filter((k: any) => k.api_key && k.api_key.length > 0).map((k: any) => k.provider);
        setVerifiedProviders(providers);
      });
    }
  }, [user]);

  const handleUpdateAiConfig = async (category: string, updates: any) => {
    setUserAiConfigs(prev => {
      const existing = prev.find((c: any) => c.category === category);
      if (existing) {
        return prev.map((c: any) => c.category === category ? { ...c, ...updates } : c);
      }
      return [...prev, {
        category,
        model_id: category === 'stock_analysis' ? 'gemini-2.5-flash' : 'gemini-2.5-pro',
        system_prompt: '',
        temperature: 0.7,
        max_tokens: 4096,
        ...updates,
      }];
    });

    setIsSavingAiConfig(category);
    try {
      const config = userAiConfigs.find((c: any) => c.category === category) || { category };
      const newConfig = { ...config, ...updates };
      await updateUserAIConfig(newConfig);
    } catch (err) {
      console.error('Failed to update AI config', err);
      fetchUserAIConfig().then(data => {
        if (Array.isArray(data)) setUserAiConfigs(data);
      });
    } finally {
      setTimeout(() => setIsSavingAiConfig(null), 1000);
    }
  };

  const handleToggleAiPersonalization = async () => {
    const newValue = !aiPersonalizationEnabled;
    setAiPersonalizationEnabled(newValue);
    try {
      await updateUserAIConfig({
        category: '_personalization_toggle',
        model_id: newValue ? 'on' : 'off'
      });
    } catch (err) {
      console.error('Failed to toggle AI personalization', err);
      setAiPersonalizationEnabled(!newValue);
    }
  };

  const handleUpdateApiKey = async (provider: string, apiKey: string) => {
    setIsSavingApiKey(provider);
    try {
      await updateUserApiKey(provider, apiKey);
      setUserApiKeys(prev => {
        const existing = prev.find(k => k.provider === provider);
        if (existing) return prev.map(k => k.provider === provider ? { ...k, api_key: apiKey } : k);
        return [...prev, { provider, api_key: apiKey }];
      });
      if (apiKey && apiKey.length > 0) {
        setVerifiedProviders(prev => prev.includes(provider) ? prev : [...prev, provider]);
      }
    } catch (err) {
      console.error('Failed to update API key', err);
    } finally {
      setTimeout(() => setIsSavingApiKey(null), 1000);
    }
  };

  const handleToggleNotifications = async () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    try {
      await updateUserProfile({ notificationsEnabled: newValue });
      if (onUpdateProfile) onUpdateProfile({ notificationsEnabled: newValue });
    } catch (err) {
      console.error('Failed to update notification settings', err);
      setNotificationsEnabled(!newValue);
    }
  };

  const handleSaveAlertSettings = async () => {
    setIsSavingAlerts(true);
    try {
      const updates = {
        vixThreshold,
        mddThreshold,
        bondYieldThreshold,
        usdKrwResistance,
        usdKrwSupport,
        stopLossThreshold,
        concentrationThreshold,
        oilSpikeThreshold,
        vixRecoveryAlertEnabled,
        executionAlertEnabled,
        billingAlertEnabled
      };
      await updateUserProfile(updates);
      if (onUpdateProfile) {
        onUpdateProfile(updates);
      }
    } catch (err) {
      console.error('Failed to update alert settings', err);
    } finally {
      setTimeout(() => setIsSavingAlerts(false), 800);
    }
  };

  const handleUiLanguageChange = async (newLang: 'ko' | 'en') => {
    setUiLanguage(newLang);
    try {
      await updateUserProfile({ uiLanguage: newLang });
      if (onUpdateProfile) onUpdateProfile({ uiLanguage: newLang });
    } catch (err) {
      console.error('Failed to update UI language settings', err);
    }
  };

  const handleAnalysisLanguageChange = async (newLang: 'ko' | 'en') => {
    setAnalysisLanguage(newLang);
    try {
      await updateUserProfile({ analysisLanguage: newLang });
      if (onUpdateProfile) onUpdateProfile({ analysisLanguage: newLang });
    } catch (err) {
      console.error('Failed to update analysis language settings', err);
    }
  };

  const handleSaveTargetGoal = async (type: 'goal' | 'rebalance' = 'goal') => {
    if (type === 'goal') {
      setIsSavingGoal(true);
    } else {
      setIsSavingRebalance(true);
    }
    try {
      const updates = {
        targetGoalAmountUS,
        targetGoalAmountKR,
        investorType,
        investmentYears,
        investorExperienceTier,
        activeSandboxMode,
        rebalanceInterval
      };
      await updateUserProfile(updates);
      if (onUpdateProfile) {
        onUpdateProfile(updates);
      }
    } catch (err) {
      console.error('Failed to update target goal amount', err);
    } finally {
      setTimeout(() => {
        if (type === 'goal') {
          setIsSavingGoal(false);
        } else {
          setIsSavingRebalance(false);
        }
      }, 800);
    }
  };

  // Sync local state if props change
  useEffect(() => {
    setLocalKeys(apiKeys);
  }, [apiKeys]);

  const handleSaveKeys = () => {
    onSaveApiKeys(localKeys);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const totalTargetWeight = holdings.reduce((acc, h) => acc + (h.targetWeight > 1 ? h.targetWeight / 100 : (h.targetWeight || 0)), 0);
  const isTierExpired = !!(userProfile?.tierExpiresAt && new Date(userProfile.tierExpiresAt) < new Date());
  
  const hasAutoTradingAccess = Boolean(
    gateAutoTradingAccess ||
    (isLiveMode
      ? (checkAddonAccess('addon_smart_order_vip') ||
         checkAddonAccess('addon_smart_order_pro') ||
         checkAddonAccess('addon_smart_order_standard'))
      : (checkAddonAccess('addon_agent_vip') ||
         checkAddonAccess('addon_agent_pro') ||
         checkAddonAccess('addon_agent_standard')))
  );
  const hasOptionsAnalyticsAccess = checkAddonAccess('OPTIONS_ANALYTICS_ADDON');
  const hasAiPersonalizationAccess = checkAddonAccess('AI_PERSONALIZATION_ADDON');
  const hasDeveloperApiAccess = checkAddonAccess('API_ACCESS_ADDON');

  return (
    <div className="space-y-8 max-w-4xl mx-auto pb-20">
      {/* 1. Interface Options & Language Settings */}
      <CollapsibleSection
        id="section-display-lang"
        variant="glass"
        title={uiLanguage === 'ko' ? '기본 화면 및 언어 설정' : 'Interface & Language Settings'}
        subtitle={uiLanguage === 'ko' ? '서비스 언어, 리포트 언어, 평가 통화 및 대시보드 글자 크기를 설정합니다.' : 'Configure UI language, AI report output, display currency, and font size.'}
        icon={<Languages className="w-5 h-5 text-purple-400" />}
        isOpen={!collapsedSections['display_lang']}
        onToggle={() => toggleSection('display_lang')}
        contentClassName="space-y-6"
      >
        {/* UI Language Selection */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/5 gap-4">
          <div className="space-y-1">
            <h3 className="text-white font-bold text-base">{uiLanguage === 'ko' ? '서비스 인터페이스 언어' : 'Service Interface Language'}</h3>
            <p className="text-white/40 text-xs">{uiLanguage === 'ko' ? '메뉴, 버튼 등 서비스 화면의 언어를 선택합니다.' : 'Select the language for menus, buttons, and overall UI.'}</p>
          </div>
          <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 w-fit shrink-0">
            <button
              onClick={() => handleUiLanguageChange('ko')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${uiLanguage === 'ko' ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white/60'}`}
            >
              한국어
            </button>
            <button
              onClick={() => handleUiLanguageChange('en')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${uiLanguage === 'en' ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white/60'}`}
            >
              English
            </button>
          </div>
        </div>

        {/* AI Analysis Language Selection */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/5 gap-4">
          <div className="space-y-1">
            <h3 className="text-white font-bold text-base">{uiLanguage === 'ko' ? 'AI 분석 리포트 언어' : 'AI Analysis Report Language'}</h3>
            <p className="text-white/40 text-xs">{uiLanguage === 'ko' ? 'AI 리포트 및 분석 결과의 출력 언어를 선택합니다.' : 'Select the language for AI reports and analysis results.'}</p>
          </div>
          <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 w-fit shrink-0">
            <button
              onClick={() => handleAnalysisLanguageChange('ko')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${analysisLanguage === 'ko' ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white/60'}`}
            >
              한국어
            </button>
            <button
              onClick={() => handleAnalysisLanguageChange('en')}
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${analysisLanguage === 'en' ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white/60'}`}
            >
              English
            </button>
          </div>
        </div>

        {/* Display Currency Preference Selection */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/5 gap-4">
          <div className="space-y-1">
            <h3 className="text-white font-bold text-base">{uiLanguage === 'ko' ? '자산 평가 통화 설정' : 'Portfolio Display Currency'}</h3>
            <p className="text-white/40 text-xs">{uiLanguage === 'ko' ? '포트폴리오의 총 가치 및 자산 가치를 표시할 통화를 선택합니다.' : 'Select the currency to display your portfolio total value and valuations.'}</p>
          </div>
          <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 w-fit shrink-0">
            {(['USD', 'KRW'] as const).map((pref) => (
              <button
                key={pref}
                onClick={() => onCurrencyPreferenceChange?.(pref)}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${currencyPreference === pref ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white/60'}`}
              >
                {pref}
              </button>
            ))}
          </div>
        </div>

        {/* Dashboard Font Size */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/5 gap-4">
          <div className="space-y-1">
            <h3 className="text-white font-bold text-base">{uiLanguage === 'ko' ? '대시보드 글자 크기' : 'Dashboard Font Size'}</h3>
            <p className="text-white/40 text-xs">{uiLanguage === 'ko' ? '대시보드 전체의 기본 글자 크기를 조정합니다. 변경 즉시 적용됩니다.' : 'Adjusts the base font size across the dashboard. Applied immediately.'}</p>
          </div>
          <div className="flex bg-white/5 p-1 rounded-xl border border-white/10 w-fit shrink-0 overflow-x-auto">
            {FONT_SIZE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleFontSizeChange(opt.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${dashboardFontSize === opt.value ? 'bg-white/10 text-white shadow-lg' : 'text-white/40 hover:text-white/60'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </CollapsibleSection>

      {/* 2. Personalized Investor Type & Investment Goal */}
      <CollapsibleSection
        id="section-invest-goal"
        variant="card"
        title={uiLanguage === 'ko' ? '초개인화 투자 성향 및 목표' : 'Investor Profile & Target Goals'}
        subtitle={uiLanguage === 'ko' ? '투자 성향과 목표 금액에 맞춰 AI가 리스크 관리 및 자산 배분을 최적화합니다.' : 'Optimizes AI risk management based on your risk tolerance and asset size target.'}
        icon={<Target className="w-5 h-5 text-emerald-400" />}
        isOpen={!collapsedSections['invest_goal']}
        onToggle={() => toggleSection('invest_goal')}
        contentClassName="space-y-6"
      >
        {/* Investor Type Selection Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { level: 1, label_ko: '신중형', label_en: 'Very Conservative', color: 'text-blue-400 border-blue-500/30 bg-blue-500/20', dot: 'bg-blue-400', desc_ko: '원금 보존 최우선. 자본 손실 회피.', desc_en: 'Capital preservation first. Avoid capital loss.' },
            { level: 2, label_ko: '안정추구형', label_en: 'Conservative', color: 'text-teal-400 border-teal-500/30 bg-teal-500/20', dot: 'bg-teal-400', desc_ko: '원금 손실 최소화. 물가상승률 방어 목적.', desc_en: 'Minimize capital loss. Hedge against inflation.' },
            { level: 3, label_ko: '위험중립형', label_en: 'Moderate', color: 'text-emerald-400 border-emerald-500/30 bg-emerald-500/20', dot: 'bg-emerald-400', desc_ko: '시장 수익률 추구. 적절한 위험 감수.', desc_en: 'Seeking market returns. Accepting moderate risk.' },
            { level: 4, label_ko: '적극투자형', label_en: 'Aggressive', color: 'text-orange-400 border-orange-500/30 bg-orange-500/20', dot: 'bg-orange-400', desc_ko: '투자원금 일부 손실 감내. 높은 수익 추구.', desc_en: 'Willing to accept some capital loss for high returns.' },
            { level: 5, label_ko: '공격투자형', label_en: 'Very Aggressive', color: 'text-red-400 border-red-500/30 bg-red-500/20', dot: 'bg-red-400', desc_ko: '시장 대폭 상회 기대. 원금 손실 위험 적극 감수.', desc_en: 'Expect high outperformance. Willing to take high risks.' }
          ].map((type) => (
            <div
              key={type.level}
              onClick={async () => {
                setInvestorType(type.level);
                try {
                  await updateUserProfile({ investorType: type.level });
                  if (onUpdateProfile) onUpdateProfile({ investorType: type.level });
                } catch (e) {
                  console.error('Failed to update investor type');
                }
              }}
              className={`flex flex-col items-start justify-between p-5 rounded-2xl border cursor-pointer transition-all ${investorType === type.level
                ? 'bg-white/10 border-white/30 shadow-lg scale-[1.01] ring-2 ring-white/20'
                : 'bg-white/[0.02] border-white/5 hover:bg-white/[0.05] hover:border-white/10 opacity-70 hover:opacity-100'
                }`}
            >
              <div className="flex items-center justify-between w-full mb-3">
                <span className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${type.color}`}>
                  LV.{type.level}
                </span>
                {investorType === type.level && <div className={`w-2 h-2 rounded-full animate-pulse ${type.dot}`} />}
              </div>
              <div className="space-y-1 mb-2">
                <h3 className="font-bold text-sm text-white">{uiLanguage === 'ko' ? type.label_ko : type.label_en}</h3>
              </div>
              <p className="text-[11px] text-white/40 leading-relaxed">
                {uiLanguage === 'ko' ? type.desc_ko : type.desc_en}
              </p>
            </div>
          ))}
        </div>

        {/* Goal settings */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
          <div className="bg-white/5 border border-white/10 p-6 rounded-[2rem] relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 blur-[60px] -mr-16 -mt-16" />
            <div className="relative space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-purple-500/10 rounded-xl border border-purple-500/20">
                  <Target className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <span className="text-[10px] font-black text-white/20 uppercase tracking-[0.2em] block">US Strategy 🇺🇸</span>
                  <span className="text-[11px] text-white/40 block mt-0.5">{uiLanguage === 'ko' ? '달러(USD) 기준 목표 금액' : 'Set target asset size in USD'}</span>
                </div>
              </div>
              
              <div className="space-y-1">
                <div className="relative flex items-center">
                  <span className="absolute left-4 text-2xl font-black text-white/30 italic">$</span>
                  <input
                    type="text"
                    value={targetGoalAmountUS ? formatNumberWithCommas(targetGoalAmountUS) : ''}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9]/g, '');
                      setTargetGoalAmountUS(raw ? Number(raw) : 0);
                    }}
                    placeholder={uiLanguage === 'ko' ? '목표 달러 입력' : 'Enter USD amount'}
                    className="w-full bg-black/40 border border-white/5 focus:border-purple-500/30 rounded-2xl pl-12 pr-4 py-3.5 text-2xl font-black text-white tracking-tighter italic outline-none transition-all font-mono"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 p-6 rounded-[2rem] relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 blur-[60px] -mr-16 -mt-16" />
            <div className="relative space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-500/10 rounded-xl border border-blue-500/20">
                  <PieChart className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <span className="text-[10px] font-black text-white/20 uppercase tracking-[0.2em] block">KR Strategy 🇰🇷</span>
                  <span className="text-[11px] text-white/40 block mt-0.5">{uiLanguage === 'ko' ? '원화(KRW) 기준 목표 금액' : 'Set target asset size in KRW'}</span>
                </div>
              </div>
              
              <div className="space-y-1">
                <div className="relative flex items-center">
                  <span className="absolute left-4 text-2xl font-black text-white/30 italic">₩</span>
                  <input
                    type="text"
                    value={targetGoalAmountKR ? formatNumberWithCommas(targetGoalAmountKR) : ''}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9]/g, '');
                      setTargetGoalAmountKR(raw ? Number(raw) : 0);
                    }}
                    placeholder={uiLanguage === 'ko' ? '목표 원화 입력' : 'Enter KRW amount'}
                    className="w-full bg-black/40 border border-white/5 focus:border-blue-500/30 rounded-2xl pl-12 pr-4 py-3.5 text-2xl font-black text-white tracking-tighter italic outline-none transition-all font-mono"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <button
            onClick={() => handleSaveTargetGoal('goal')}
            disabled={isSavingGoal || isLoadingProfile}
            className={`px-8 py-3 bg-white text-black hover:bg-emerald-500 hover:text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50`}
          >
            {isSavingGoal ? (uiLanguage === 'ko' ? '저장 중...' : 'Saving...') : (uiLanguage === 'ko' ? '목표 금액 저장' : 'Save Goals')}
          </button>
        </div>
      </CollapsibleSection>

      {/* 3. Rebalancing Interval Settings */}
      <CollapsibleSection
        id="section-rebalance"
        variant="card"
        title={uiLanguage === 'ko' ? '포트폴리오 리밸런싱 주기 설정' : 'Portfolio Rebalancing Cycle'}
        subtitle={uiLanguage === 'ko' ? '정기 리밸런싱 주기에 따라 캘린더 이벤트 및 알림이 연동됩니다.' : 'Recurring rebalancing events will populate your calendar and trigger notifications.'}
        icon={<Calendar className="w-5 h-5 text-emerald-400" />}
        isOpen={!collapsedSections['rebalance']}
        onToggle={() => toggleSection('rebalance')}
        contentClassName="space-y-6"
      >
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/5 gap-6">
            <div className="space-y-1 flex-1">
              <h3 className="text-white font-bold text-base">
                {uiLanguage === 'ko' ? '정기 리밸런싱 주기' : 'Rebalancing Interval'}
              </h3>
              <p className="text-white/40 text-xs">
                {uiLanguage === 'ko'
                  ? '설정한 주기에 따라 포트폴리오 캘린더에 정기 리밸런싱 일정이 표시되며 알림이 전송됩니다.'
                  : 'Based on the selected cycle, recurring rebalancing events will populate your calendar and trigger notifications.'}
              </p>
            </div>
            <div className="w-full md:w-auto shrink-0">
              <select
                value={rebalanceInterval.startsWith('manual') ? 'manual' : rebalanceInterval}
                onChange={(e) => {
                  if (e.target.value === 'manual') {
                    const existingDate = rebalanceInterval.startsWith('manual:') ? rebalanceInterval.split(':')[1] : '';
                    const todayStr = existingDate || new Date().toISOString().split('T')[0];
                    setRebalanceInterval(`manual:${todayStr}`);
                  } else {
                    setRebalanceInterval(e.target.value);
                  }
                }}
                className="w-full md:w-64 bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:border-emerald-500 outline-none transition-all text-white text-sm font-bold"
              >
                <option value="weekly">{uiLanguage === 'ko' ? '매주 (Weekly)' : 'Weekly'}</option>
                <option value="monthly">{uiLanguage === 'ko' ? '매월 (Monthly)' : 'Monthly'}</option>
                <option value="quarterly">{uiLanguage === 'ko' ? '매분기 (Quarterly)' : 'Quarterly'}</option>
                <option value="half-yearly">{uiLanguage === 'ko' ? '매반기 (Half-yearly)' : 'Half-yearly'}</option>
                <option value="annually">{uiLanguage === 'ko' ? '매년 (Annually)' : 'Annually'}</option>
                <option value="manual">{uiLanguage === 'ko' ? '수동 설정 (Manual)' : 'Manual'}</option>
                <option value="none">{uiLanguage === 'ko' ? '설정 안함 (None)' : 'None'}</option>
              </select>
            </div>
          </div>

          {rebalanceInterval.startsWith('manual') && (
            <div className="flex flex-col md:flex-row items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/5 gap-6 mt-4">
              <div className="space-y-1 flex-1">
                <h3 className="text-white font-bold text-base">
                  {uiLanguage === 'ko' ? '수동 리밸런싱 날짜 선택' : 'Select Manual Rebalancing Date'}
                </h3>
                <p className="text-white/40 text-xs">
                  {uiLanguage === 'ko'
                    ? '캘린더에 정기 리밸런싱 일정으로 등록할 특정 날짜를 선택합니다.'
                    : 'Choose a specific date to populate as a rebalancing event on your calendar.'}
                </p>
              </div>
              <div className="w-full md:w-auto shrink-0">
                <input
                  type="date"
                  value={rebalanceInterval.split(':')[1] || ''}
                  onChange={(e) => setRebalanceInterval(`manual:${e.target.value}`)}
                  className="w-full md:w-64 bg-black/50 border border-white/10 rounded-xl px-4 py-2.5 focus:border-emerald-500 outline-none transition-all text-white text-sm font-bold"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={() => handleSaveTargetGoal('rebalance')}
              disabled={isSavingRebalance || isLoadingProfile}
              className={`px-8 py-3 bg-white text-black hover:bg-emerald-500 hover:text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50`}
            >
              {isSavingRebalance ? (uiLanguage === 'ko' ? '저장 중...' : 'Saving...') : (uiLanguage === 'ko' ? '설정 저장' : 'Save Settings')}
            </button>
          </div>
        </div>
      </CollapsibleSection>

      {/* 4. Target Weights Settings */}
      <CollapsibleSection
        id="section-target-weights"
        variant="card"
        title={
          <div className="flex items-center justify-between w-full pr-4">
            <span>{uiLanguage === 'ko' ? '목표 투자 비중 설정 (Target Weights)' : 'Portfolio Target Weights'}</span>
            <div className={`px-3 py-1 text-xs font-bold border rounded-xl shrink-0 ${Math.abs(totalTargetWeight - 1) > 0.01 ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'}`}>
              Total: {(totalTargetWeight * 100).toFixed(1)}%
            </div>
          </div>
        }
        subtitle={uiLanguage === 'ko' ? '각 보유 자산의 목표 투자 비중을 백분율(%)로 조절합니다.' : 'Adjust target allocation weights for your current holdings.'}
        icon={<PieChart className="w-5 h-5 text-emerald-400" />}
        isOpen={!collapsedSections['target_weights']}
        onToggle={() => toggleSection('target_weights')}
        contentClassName="space-y-4"
      >
        <div className="space-y-4 pt-2">
          {holdings.length === 0 ? (
            <div className="py-8 text-center text-xs text-white/30 font-medium">
              {uiLanguage === 'ko' ? '보유 자산이 없습니다. 먼저 자산을 추가하거나 연동해주세요.' : 'No holdings in your portfolio. Please add or link assets first.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-h-[300px] overflow-y-auto pr-1">
              {holdings.map((h) => {
                const curWeight = h.targetWeight !== undefined ? h.targetWeight : 0;
                const displayPercent = curWeight > 1 ? Number(curWeight.toFixed(2)) : Number((curWeight * 100).toFixed(2));
                return (
                  <div key={h.symbol} className="p-4 bg-white/5 border border-white/5 rounded-xl flex items-center justify-between text-xs font-semibold">
                    <div className="text-left">
                      <span className="text-white block font-bold font-mono">{h.symbol}</span>
                      <span className="text-white/40 text-[10px] block mt-0.5">{h.name || h.symbol}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={displayPercent}
                        onChange={(e) => {
                          const val = Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                          onUpdateTargetWeight(h.symbol, val);
                        }}
                        className="w-20 bg-black/40 border border-white/10 rounded-lg px-2.5 py-1.5 focus:border-emerald-500 outline-none text-right font-mono text-white"
                      />
                      <span className="text-white/40">%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CollapsibleSection>

      {/* 5. Notification & Alert Settings */}
      <CollapsibleSection
        id="section-notifications"
        variant="card"
        title={uiLanguage === 'ko' ? '실시간 지표 경보 설정' : 'Alert & Notification Settings'}
        subtitle={uiLanguage === 'ko' ? 'VIX 지수 급등, 자산 급락 및 대규모 시장 변동 경보 수신 설정.' : 'Configure push alerts on critical VIX spikes and drawdown thresholds.'}
        icon={<Bell className="w-5 h-5 text-blue-400" />}
        isOpen={!collapsedSections['notifications']}
        onToggle={() => toggleSection('notifications')}
        contentClassName="space-y-6"
      >
        <div className="flex items-center justify-between p-6 bg-white/5 rounded-2xl border border-white/5">
          <div className="space-y-1">
            <h3 className="text-white font-bold text-base flex items-center gap-1.5">
              {uiLanguage === 'ko' ? '실시간 마켓 리스크 경보' : 'Real-time Market Alert Notifications'}
              {!limits.canReceivePushAlerts && (
                <span className="text-[9px] bg-white/10 border border-white/10 px-1.5 py-0.5 rounded-full text-white/50 font-bold uppercase tracking-wider">
                  Tier 2+
                </span>
              )}
            </h3>
            <p className="text-white/40 text-xs">{uiLanguage === 'ko' ? 'VIX 지수 급등, 자산 급락 및 대규모 시장 변동 시 경보 알림을 수신합니다.' : 'Receive push alerts on critical VIX spikes and drawdown thresholds.'}</p>
          </div>
          <button
            onClick={handleToggleNotifications}
            disabled={isLoadingProfile || !limits.canReceivePushAlerts}
            className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-black ${notificationsEnabled && limits.canReceivePushAlerts ? 'bg-blue-500' : 'bg-white/10'} ${!limits.canReceivePushAlerts ? 'opacity-30 cursor-not-allowed' : ''}`}
          >
            <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${notificationsEnabled && limits.canReceivePushAlerts ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>

        {notificationsEnabled && limits.canReceivePushAlerts && (
          <div className="mt-6 border-t border-white/5 pt-6 space-y-4 transition-all duration-300">
            <h4 className="text-white/60 font-bold text-xs uppercase tracking-wider">
              {uiLanguage === 'ko' ? '실시간 경보 임계값 설정' : 'Custom Alert Thresholds'}
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? 'VIX 지수 경보선' : 'VIX Warning Threshold'}</label>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? 'VIX 급등 수준을 지정합니다.' : 'Alert on VIX spikes above.'}</p>
                </div>
                <input
                  type="number"
                  step="0.5"
                  value={vixThreshold}
                  onChange={(e) => setVixThreshold(Number(e.target.value))}
                  className="w-24 bg-black/50 border border-white/10 rounded-xl px-3 py-1.5 focus:border-blue-500 outline-none font-mono text-white text-right"
                />
              </div>

              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? '포트폴리오 MDD 경고선 (%)' : 'MDD Alert Threshold (%)'}</label>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? '고점 대비 허용 최대 낙폭입니다.' : 'Drawdown warning line.'}</p>
                </div>
                <input
                  type="number"
                  step="0.5"
                  max="0"
                  value={mddThreshold}
                  onChange={(e) => setMddThreshold(Number(e.target.value))}
                  className="w-24 bg-black/50 border border-white/10 rounded-xl px-3 py-1.5 focus:border-blue-500 outline-none font-mono text-white text-right"
                />
              </div>

              {/* Portfolio High-Water Mark (HWM) Synchronization Card */}
              <div className="p-4 bg-gradient-to-r from-blue-950/20 via-black/40 to-purple-950/20 rounded-xl border border-blue-500/20 space-y-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <label className="text-white font-bold text-xs">
                        {uiLanguage === 'ko' ? '포트폴리오 고점(HWM) 기준 동기화' : 'Portfolio HWM Baseline Synchronization'}
                      </label>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 font-semibold border border-blue-500/30">
                        {uiLanguage === 'ko' ? '실시간 감시 기준' : 'Live Monitor'}
                      </span>
                    </div>
                    <p className="text-white/40 text-[10px]">
                      {uiLanguage === 'ko'
                        ? '입출금 또는 종목 매도/삭제 후 고점 대비 낙폭이 비정상적으로 치솟는 것을 방지합니다.'
                        : 'Aligns peak reference with current portfolio scale after deposits/withdrawals.'}
                    </p>
                  </div>

                  <button
                    onClick={() => handleResetHwm()}
                    disabled={isResettingHwm}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all shadow-md active:scale-95 shrink-0"
                  >
                    <RotateCcw className={`w-3.5 h-3.5 ${isResettingHwm ? 'animate-spin' : ''}`} />
                    {isResettingHwm
                      ? (uiLanguage === 'ko' ? '동기화 중...' : 'Resetting...')
                      : (uiLanguage === 'ko' ? '현재 자산으로 재동기화' : 'Sync to Current Value')}
                  </button>
                </div>

                {hwmStatus && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-white/5">
                    <div className="bg-black/40 rounded-lg p-2 border border-white/5">
                      <span className="text-[10px] text-white/40 block">{uiLanguage === 'ko' ? '역대 최고점 (DB)' : 'All-time Peak'}</span>
                      <span className="text-xs font-mono font-bold text-white">${hwmStatus.allTimeMax.toLocaleString()}</span>
                    </div>
                    <div className="bg-black/40 rounded-lg p-2 border border-white/5">
                      <span className="text-[10px] text-white/40 block">{uiLanguage === 'ko' ? '현재 감시 고점' : 'Active Peak (HWM)'}</span>
                      <span className="text-xs font-mono font-bold text-blue-400">${hwmStatus.hwm.toLocaleString()}</span>
                    </div>
                    <div className="bg-black/40 rounded-lg p-2 border border-white/5">
                      <span className="text-[10px] text-white/40 block">{uiLanguage === 'ko' ? '현재 평가액' : 'Current Value'}</span>
                      <span className="text-xs font-mono font-bold text-emerald-400">${hwmStatus.currentPortfolioValue.toLocaleString()}</span>
                    </div>
                    <div className="bg-black/40 rounded-lg p-2 border border-white/5">
                      <span className="text-[10px] text-white/40 block">{uiLanguage === 'ko' ? '고점 대비 낙폭' : 'Drawdown'}</span>
                      <span className={`text-xs font-mono font-bold ${hwmStatus.drawdownPct <= mddThreshold ? 'text-red-400' : 'text-white/80'}`}>
                        {hwmStatus.drawdownPct > 0 ? `+${hwmStatus.drawdownPct}%` : `${hwmStatus.drawdownPct}%`}
                      </span>
                    </div>
                  </div>
                )}

                {hwmFeedback && (
                  <div className="text-[11px] font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-lg flex items-center gap-1.5 animate-fadeIn">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    <span>{hwmFeedback}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? '미국 10년물 국채 금리 일일 변동폭 (%p)' : 'US 10Y Yield Daily Move'}</label>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? '10bp = 0.10' : 'Yield change threshold.'}</p>
                </div>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={bondYieldThreshold}
                  onChange={(e) => setBondYieldThreshold(Number(e.target.value))}
                  className="w-24 bg-black/50 border border-white/10 rounded-xl px-3 py-1.5 focus:border-blue-500 outline-none font-mono text-white text-right"
                />
              </div>

              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? '환율 지지/저항 모니터링선' : 'USD/KRW Support/Resistance'}</label>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? '이탈 감시 환율 범위 설정' : 'Alert on range exit.'}</p>
                </div>
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="5"
                    value={usdKrwSupport}
                    onChange={(e) => setUsdKrwSupport(Number(e.target.value))}
                    className="w-20 bg-black/50 border border-white/10 rounded-xl px-2.5 py-1.5 focus:border-blue-500 outline-none font-mono text-white text-right text-xs"
                  />
                  <span className="text-white/30 text-[10px]">~</span>
                  <input
                    type="number"
                    step="5"
                    value={usdKrwResistance}
                    onChange={(e) => setUsdKrwResistance(Number(e.target.value))}
                    className="w-20 bg-black/50 border border-white/10 rounded-xl px-2.5 py-1.5 focus:border-blue-500 outline-none font-mono text-white text-right text-xs"
                  />
                </div>
              </div>

              {/* 1. Portfolio Risk: Stop Loss Threshold */}
              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? (isLiveMode ? '개별 보유종목 손절 경고선 (%)' : '개별 보유종목 하방 변동성 경고선 (%)') : 'Individual Asset Drawdown Alert (%)'}</label>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-semibold border border-amber-500/30">P1 Defense</span>
                  </div>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? '기준 단가 대비 지정 낙폭 도달 시 리스크 점검 알림을 전송합니다.' : 'Trigger alert when any holding falls below threshold from reference price.'}</p>
                </div>
                <input
                  type="number"
                  step="1"
                  max="0"
                  value={stopLossThreshold}
                  onChange={(e) => setStopLossThreshold(Number(e.target.value))}
                  className="w-24 bg-black/50 border border-white/10 rounded-xl px-3 py-1.5 focus:border-blue-500 outline-none font-mono text-white text-right"
                />
              </div>

              {/* 1. Portfolio Risk: Concentration Threshold */}
              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? '단일 종목 집중도 리스크 경고선 (%)' : 'Single Asset Max Allocation (%)'}</label>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-semibold border border-blue-500/30">P2 Allocation</span>
                  </div>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? '한 종목의 포트폴리오 비중이 지정 비율 초과 시 비중 편차 알림을 전송합니다.' : 'Warns when a single holding exceeds allocation limit.'}</p>
                </div>
                <input
                  type="number"
                  step="5"
                  min="10"
                  max="100"
                  value={concentrationThreshold}
                  onChange={(e) => setConcentrationThreshold(Number(e.target.value))}
                  className="w-24 bg-black/50 border border-white/10 rounded-xl px-3 py-1.5 focus:border-blue-500 outline-none font-mono text-white text-right"
                />
              </div>

              {/* 2. Macro Shock: WTI Oil Spike Threshold */}
              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? '국제 유가(WTI) 일일 급등 경고선 (%)' : 'WTI Crude Oil Spike Alert (%)'}</label>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-300 font-semibold border border-orange-500/30">Macro Shock</span>
                  </div>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? '지정학적 리스크 및 인플레이션 압력 급등 시 경고 알림을 발송합니다.' : 'Alert on geopolitical or supply-driven oil spikes.'}</p>
                </div>
                <input
                  type="number"
                  step="0.5"
                  min="1"
                  value={oilSpikeThreshold}
                  onChange={(e) => setOilSpikeThreshold(Number(e.target.value))}
                  className="w-24 bg-black/50 border border-white/10 rounded-xl px-3 py-1.5 focus:border-blue-500 outline-none font-mono text-white text-right"
                />
              </div>

              {/* 3. Alpha Opportunity: VIX Extreme Fear Recovery Alert */}
              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? (isLiveMode ? '공포 해소 반등(저점 매수 기회) 알림' : '공포 해소 반등(변동성 진정 국면 전환) 알림') : 'VIX Recovery Regime Signal'}</label>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-semibold border border-emerald-500/30">Alpha Signal</span>
                  </div>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? 'VIX가 극단 공포(30+) 후 하향 안정화될 때 변동성 완화 모멘텀을 포착합니다.' : 'Notifies when VIX cools down from panic levels into a stabilizing regime.'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setVixRecoveryAlertEnabled(!vixRecoveryAlertEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors outline-none shrink-0 ${vixRecoveryAlertEnabled ? 'bg-blue-500' : 'bg-white/10'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${vixRecoveryAlertEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* 4. Execution / Auto-Trading Alerts */}
              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? '시그널 웹훅 알림 & 모의 분석 알림' : 'Signal Webhook & Simulation Alerts'}</label>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-semibold border border-purple-500/30">Webhook</span>
                  </div>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? '기술 지표 조건 도달 시 텔레그램/웹 알림 및 모의 분석 결과를 전달받습니다.' : 'Immediate notifications for technical indicator triggers and sandbox simulations.'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setExecutionAlertEnabled(!executionAlertEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors outline-none shrink-0 ${executionAlertEnabled ? 'bg-blue-500' : 'bg-white/10'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${executionAlertEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              {/* 5. Subscription & Billing Alerts */}
              <div className="flex justify-between items-center p-4 bg-white/[0.02] rounded-xl border border-white/5 gap-4">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="text-white font-bold text-xs">{uiLanguage === 'ko' ? '구독 정기 결제 D-3 사전 안내' : 'Subscription Renewal D-3 Notification'}</label>
                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-semibold border border-cyan-500/30">Account</span>
                  </div>
                  <p className="text-white/40 text-[10px]">{uiLanguage === 'ko' ? '멤버십 갱신 3일 전 결제 예정 금액 및 플랜 상태를 안내합니다.' : 'Receive notice 3 days before upcoming subscription charge.'}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setBillingAlertEnabled(!billingAlertEnabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors outline-none shrink-0 ${billingAlertEnabled ? 'bg-blue-500' : 'bg-white/10'}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${billingAlertEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                onClick={handleSaveAlertSettings}
                className="px-8 py-3 bg-white text-black hover:bg-emerald-500 hover:text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all"
              >
                {isSavingAlerts ? (uiLanguage === 'ko' ? '저장 중...' : 'Saving...') : (uiLanguage === 'ko' ? '설정 저장' : 'Save Alerts')}
              </button>
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* AI Quant Auto-Trading Settings Section */}
      {(() => {
        const autoTradingProduct = activeAddons.find(a =>
          isLiveMode
            ? (a.id === 'addon_smart_order_pro' || a.id === 'addon_smart_order_vip' || a.id === 'addon_smart_order_standard')
            : (a.id === 'addon_agent_pro' || a.id === 'addon_agent_vip' || a.id === 'addon_agent_standard')
        );
        const isAutoTradingActive = activeAddons.length === 0 || !!autoTradingProduct;
        const autoTradingMinTier: MembershipTier = (autoTradingProduct?.minTier as MembershipTier) || 'TIER5';
        const isAutoTradingTierSufficient = canAccessTier(normalizedUserTier as any, autoTradingMinTier);

        let currentTierFreeAddOns: string[] = [];
        if (productsConfig && productsConfig[normalizedUserTier]) {
          currentTierFreeAddOns = productsConfig[normalizedUserTier].freeAddOns || [];
        } else {
          if (normalizedUserTier === 'TIER5') {
            currentTierFreeAddOns = ['CHAT_PREDICTION_ADDON', 'API_ACCESS_ADDON', 'AI_PERSONALIZATION_ADDON', 'OPTIONS_ANALYTICS_ADDON'];
          } else if (normalizedUserTier === 'TIER4') {
            currentTierFreeAddOns = ['AI_PERSONALIZATION_ADDON'];
          }
        }
        const isAutoTradingFree = (isLiveMode
          ? (currentTierFreeAddOns.includes('addon_smart_order_pro') || currentTierFreeAddOns.includes('addon_smart_order_vip'))
          : (currentTierFreeAddOns.includes('addon_agent_pro') || currentTierFreeAddOns.includes('addon_agent_vip'))) && !isTierExpired;

        if (!isAutoTradingActive || !hasAutoTradingAccess) return null;

        return (
          <CollapsibleSection
            id="section-autotrade"
            variant="card"
            title={isLiveMode
              ? (uiLanguage === 'ko' ? '스마트 주문 설정' : 'Smart Order Settings')
              : (uiLanguage === 'ko' ? '시그널 레이더 설정' : 'Signal Radar Settings')}
            subtitle={isLiveMode
              ? (uiLanguage === 'ko' ? 'AI 매크로 및 퀀트 엔진 시그널에 기반하여 연동 실계좌로 주문을 집행하는 스마트 주문 환경을 설정합니다.' : 'Configure live Smart Order execution on your linked brokerage account based on AI macro/quant signals.')
              : (uiLanguage === 'ko' ? '실시간 퀀트 시그널 웹훅 발송 및 가상 샌드박스 시뮬레이션 환경을 설정합니다.' : 'Configure real-time quant signal webhooks and virtual paper trading simulations.')}
            icon={<Sparkles className="w-5 h-5 text-cyan-400" />}
            isOpen={!collapsedSections['autotrade']}
            onToggle={() => toggleSection('autotrade')}
            headerRight={
              <div className="flex items-center gap-3">
                {hasAutoTradingAccess && isAutoTradingFree && (
                  <span className="px-3 py-1 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 text-xs font-bold rounded-lg select-none hidden sm:inline-block">
                    {uiLanguage === 'ko' ? '멤버십 혜택' : 'Membership Benefit'}
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setAutoTradeEnabled(!autoTradeEnabled); }}
                  className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-black ${autoTradeEnabled ? 'bg-cyan-500' : 'bg-white/10'}`}
                >
                  <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${autoTradeEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
                </button>
              </div>
            }
          >
            {autoTradeEnabled ? (
              isLiveMode ? (
                <div className="space-y-6 transition-all duration-300">
                  {/* LIVE 스마트 주문 설정 */}
                  {/* 실거래 안내 배너 */}
                  <div className="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-start gap-3">
                    <ShieldCheck className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    <div className="space-y-1">
                      <p className="text-xs font-bold text-emerald-300">
                        {uiLanguage === 'ko' ? '실거래 스마트 주문 실행 환경' : 'Live Smart Order Execution'}
                      </p>
                      <p className="text-[10px] text-emerald-200/70 leading-relaxed">
                        {uiLanguage === 'ko'
                          ? 'AI 매크로 및 퀀트 엔진 시그널에 기반하여 연동된 실계좌로 주문을 집행합니다. 아래 리스크 가드레일이 모든 주문에 우선 적용됩니다.'
                          : 'Executes orders on your linked live account based on AI macro/quant signals. The risk guardrails below are always enforced first.'}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* 주문 실행 모드 (HITL 1-Click 승인 vs Full-Auto 자율 실행) */}
                    <div className="flex flex-col gap-2">
                      <label className="text-white font-bold text-xs flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <ZapIcon className="w-3.5 h-3.5 text-cyan-400" />
                          {uiLanguage === 'ko' ? '주문 실행 모드' : 'Order Execution Mode'}
                        </span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                          {agentHitlPreference === 'hitl'
                            ? (uiLanguage === 'ko' ? '1-Click 승인' : '1-Click')
                            : (uiLanguage === 'ko' ? '자율 실행' : 'Full-Auto')}
                        </span>
                      </label>
                      <select
                        value={agentHitlPreference}
                        onChange={(e) => setAgentHitlPreference(e.target.value as 'hitl' | 'auto')}
                        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 focus:border-cyan-500 outline-none text-white text-sm"
                        disabled={!(hasAutoTradingAccess || (userProfile?.membershipTier === 'PRO' || userProfile?.membershipTier === 'PREMIUM'))}
                      >
                        <option value="hitl">{uiLanguage === 'ko' ? '🛡️ HITL 1-Click 승인 (수동 확인)' : 'HITL 1-Click Approval'}</option>
                        <option value="auto">{uiLanguage === 'ko' ? '⚡️ Full-Auto 자율 실행 (완전 자동)' : 'Full-Auto Execution'}</option>
                      </select>
                      <p className="text-[10px] text-white/50 leading-relaxed">
                        {agentHitlPreference === 'hitl'
                          ? (uiLanguage === 'ko' ? 'AI 시그널 발생 시 주문 초안을 생성하고, 대시보드/알림에서 1-Click 승인한 경우에만 실계좌로 집행됩니다.' : 'Creates an order draft on signal and executes on the live account only after 1-Click approval.')
                          : (uiLanguage === 'ko' ? 'AI 시그널 발생 즉시 리스크 가드레일 검증 후 실계좌로 자동 집행됩니다. (고위험)' : 'Executes automatically on the live account right after risk guardrail validation. (High risk)')}
                      </p>
                      {agentHitlPreference === 'auto' && !userApiKeys.some((k: any) => k.api_key && String(k.api_key).trim() !== '') && (
                        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-2.5">
                          <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                          <p className="text-[11px] text-amber-200/90 leading-relaxed">
                            {uiLanguage === 'ko'
                              ? '⚠️ 등록된 실거래 증권사 API Key가 없습니다. API Key가 연동되지 않은 상태에서는 실자산 보호를 위해 주문 시그널이 1-Click 승인 대기로 안전 전환됩니다. [마이페이지]에서 API Key를 연동해주세요.'
                              : '⚠️ No live broker API key registered. Order signals will safely fallback to 1-Click Approval until an API key is linked in My Page.'}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* 실거래 실행 증권사 */}
                    <div className="flex flex-col gap-2">
                      <label className="text-white font-bold text-xs flex items-center gap-2">
                        <span className="flex items-center gap-1.5">
                          <ZapIcon className="w-3.5 h-3.5 text-emerald-400" />
                          {uiLanguage === 'ko' ? '실거래 실행 증권사' : 'Live Execution Broker'}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                          LIVE
                        </span>
                      </label>
                      <select
                        value={autoTradeBroker}
                        onChange={(e) => setAutoTradeBroker(e.target.value as 'smart_routing' | 'kis' | 'ls' | 'kiwoom' | 'toss')}
                        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 focus:border-cyan-500 outline-none text-white text-sm font-semibold cursor-pointer"
                      >
                        <option value="smart_routing">{uiLanguage === 'ko' ? '🧠 스마트 라우팅 (자동 분배)' : '🧠 Smart Routing (Auto)'}</option>
                        <option value="kis">{uiLanguage === 'ko' ? '한국투자증권 · 실거래 (LIVE)' : 'KIS · Live'}</option>
                        <option value="ls">{uiLanguage === 'ko' ? 'LS증권 · 실거래 (LIVE)' : 'LS · Live'}</option>
                        <option value="kiwoom">{uiLanguage === 'ko' ? '키움증권 · 실거래 (LIVE)' : 'Kiwoom · Live'}</option>
                        <option value="toss">{uiLanguage === 'ko' ? '토스증권 · 실거래 (LIVE)' : 'Toss · Live'}</option>
                      </select>
                      <p className="text-[10px] text-emerald-400/80 leading-relaxed flex items-start gap-1.5">
                        <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" />
                        {uiLanguage === 'ko'
                          ? '실거래 모드입니다. 증권사 실거래 API 키 등록은 [마이페이지 > 실거래 증권사 및 실계좌 연동]에서 진행합니다.'
                          : 'Live trading mode. Register brokerage live API keys in [My Page > Live Brokerage & Account Linking].'}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* 에이전트 마켓 가동 일정 모드 */}
                    <div className="flex flex-col gap-2">
                      <label className="text-white font-bold text-xs flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5 text-cyan-400" />
                        {uiLanguage === 'ko' ? '에이전트 가동 일정 모드' : 'Operating Schedule Mode'}
                      </label>
                      <select
                        value={scheduleMode}
                        onChange={(e) => setScheduleMode(e.target.value as 'MARKET_HOURS_ONLY' | 'ALL_DAY')}
                        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 focus:border-cyan-500 outline-none text-white text-sm"
                      >
                        <option value="MARKET_HOURS_ONLY">
                          {uiLanguage === 'ko' ? '⏰ 장 오픈 시각 동기화 (Market-Open Auto-Pilot · 권장)' : 'Market-Open Auto-Pilot (Recommended)'}
                        </option>
                        <option value="ALL_DAY">
                          {uiLanguage === 'ko' ? '🌐 24시간 상시 가동 (All-day Surveillance)' : 'All-day Surveillance'}
                        </option>
                      </select>
                      <p className="text-[10px] text-white/40 leading-relaxed">
                        {scheduleMode === 'MARKET_HOURS_ONLY'
                          ? (uiLanguage === 'ko' ? '장 운영 시간 및 장전 사전 분석(Pre-market) 중심으로 가동됩니다. 주문은 정규장 오픈 시 안전하게 집행됩니다.' : 'Active during market hours and pre-market windows; orders execute safely at market open.')
                          : (uiLanguage === 'ko' ? '24시간 시장/매크로 시그널을 감시합니다. 세션 정책에 따라 체결 시점이 제어됩니다.' : 'Monitors market/macro signals around the clock; execution timing is governed by the session policy.')}
                      </p>
                    </div>

                    {/* 미국주식 체결 세션 + 최대 스프레드 가드레일 */}
                    <div className="flex flex-col gap-2 p-3.5 rounded-xl bg-cyan-950/20 border border-cyan-500/20">
                      <label className="text-white font-bold text-xs flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 text-cyan-400" />
                          {uiLanguage === 'ko' ? '🇺🇸 미국주식 체결 세션 정책 (슬리피지 보호)' : 'US Stock Execution Policy'}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
                          usExecutionSession === 'REGULAR_ONLY' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
                          usExecutionSession === 'PRE_MARKET' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                          'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                        }`}>
                          {usExecutionSession === 'REGULAR_ONLY' ? (uiLanguage === 'ko' ? '최적 유동성 보호' : 'Deep Liquidity') :
                           usExecutionSession === 'PRE_MARKET' ? (uiLanguage === 'ko' ? '프리마켓 허용' : 'Pre-market') :
                           (uiLanguage === 'ko' ? '⚠️ 슬리피지 주의' : 'High Slippage')}
                        </span>
                      </label>
                      <select
                        value={usExecutionSession}
                        onChange={(e) => setUsExecutionSession(e.target.value as 'REGULAR_ONLY' | 'PRE_MARKET' | 'ALLOW_DAY_MARKET')}
                        className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 focus:border-cyan-500 outline-none text-white text-sm"
                      >
                        <option value="REGULAR_ONLY">
                          {uiLanguage === 'ko' ? '⭐️ 정규장 개장 시 집행 (권장 · 슬리피지 최소)' : 'Regular Market Hours Only (Recommended · Min Slippage)'}
                        </option>
                        <option value="PRE_MARKET">
                          {uiLanguage === 'ko' ? '🌅 프리마켓 허용 (조기 진입)' : 'Allow Pre-market (Early Entry)'}
                        </option>
                        <option value="ALLOW_DAY_MARKET">
                          {uiLanguage === 'ko' ? '⚠️ 데이마켓 허용 (슬리피지 위험)' : 'Allow Day Market (Slippage Risk)'}
                        </option>
                      </select>
                      <div className="mt-1 p-3 bg-black/40 rounded-xl border border-white/10">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-white/70">{uiLanguage === 'ko' ? '최대 허용 스프레드 보호 한도' : 'Max Bid-Ask Spread Guard'}</span>
                          <span className="text-cyan-400 font-bold">{maxSpreadPct}%</span>
                        </div>
                        <input
                          type="range"
                          min="0.3"
                          max="2.0"
                          step="0.1"
                          value={maxSpreadPct}
                          onChange={(e) => setMaxSpreadPct(parseFloat(e.target.value))}
                          className="w-full accent-cyan-400 cursor-pointer"
                        />
                        <div className="flex justify-between text-[9px] text-white/30">
                          <span>0.3% ({uiLanguage === 'ko' ? '엄격' : 'Strict'})</span>
                          <span>0.8% ({uiLanguage === 'ko' ? '표준 권장' : 'Standard'})</span>
                          <span>2.0% ({uiLanguage === 'ko' ? '완화' : 'Relaxed'})</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* 실거래 리스크 가드레일 */}
                  <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/10 space-y-4">
                    <div className="flex items-center gap-2 border-b border-white/10 pb-3">
                      <ShieldCheck className="w-4 h-4 text-cyan-400" />
                      <h4 className="text-sm font-bold text-white">
                        {uiLanguage === 'ko' ? '실거래 리스크 가드레일' : 'Live Risk Guardrails'}
                      </h4>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* 실거래 주문 예산 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs flex items-center justify-between">
                          <span>{uiLanguage === 'ko' ? `실거래 주문 예산 (${autoTradeCurrency})` : `Live Order Budget (${autoTradeCurrency})`}</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${activeMarketMode === 'KR' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                            {activeMarketMode === 'KR' ? '🇰🇷 한국 증시 기준' : '🇺🇸 미국 증시 기준'}
                          </span>
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            value={activeMarketMode === 'KR' ? autoTradeBudgetKR : autoTradeBudgetUS}
                            onChange={(e) => {
                              const val = Math.max(0, parseFloat(e.target.value) || 0);
                              if (activeMarketMode === 'KR') {
                                setAutoTradeBudgetKR(val);
                              } else {
                                setAutoTradeBudgetUS(val);
                              }
                            }}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-14 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/40 text-xs font-semibold">{autoTradeCurrency}</span>
                        </div>
                        <p className="text-[10px] text-white/40">
                          {uiLanguage === 'ko' ? '실계좌에서 스마트 주문에 배정할 최대 운용 예산을 설정합니다.' : 'Maximum live capital allocated to Smart Orders.'}
                        </p>
                      </div>

                      {/* 1일 최대 주문 한도 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs flex items-center justify-between">
                          <span>{uiLanguage === 'ko' ? `1일 최대 주문 한도 (${autoTradeCurrency})` : `Daily Max Order Limit (${autoTradeCurrency})`}</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${activeMarketMode === 'KR' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                            {activeMarketMode === 'KR' ? '🇰🇷 한국 증시' : '🇺🇸 미국 증시'}
                          </span>
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            value={activeMarketMode === 'KR' ? autoTradeDailyOrderLimitKR : autoTradeDailyOrderLimitUS}
                            onChange={(e) => {
                              const val = Math.max(0, parseFloat(e.target.value) || 0);
                              if (activeMarketMode === 'KR') {
                                setAutoTradeDailyOrderLimitKR(val);
                              } else {
                                setAutoTradeDailyOrderLimitUS(val);
                              }
                            }}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-14 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/40 text-xs font-semibold">{autoTradeCurrency}</span>
                        </div>
                        <p className="text-[10px] text-white/40">
                          {uiLanguage === 'ko' ? '하루 동안 집행할 수 있는 누적 주문 금액 상한입니다.' : 'Maximum cumulative order amount executed per day.'}
                        </p>
                      </div>

                      {/* 종목별 최대 편입 비중 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs">
                          {uiLanguage === 'ko' ? '종목별 최대 편입 비중 (%)' : 'Max Weight per Stock (%)'}
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={autoTradeMaxWeight}
                            onChange={(e) => setAutoTradeMaxWeight(Math.max(1, Math.min(100, parseFloat(e.target.value) || 25)))}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-8 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/30 text-xs">%</span>
                        </div>
                      </div>

                      {/* 종목 손절 기준선 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs">
                          {uiLanguage === 'ko' ? '종목 손절 기준선 (%)' : 'Stop Loss (%)'}
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={autoTradeStopLoss}
                            onChange={(e) => setAutoTradeStopLoss(Math.max(1, Math.min(50, parseFloat(e.target.value) || 8)))}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-8 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/30 text-xs">%</span>
                        </div>
                      </div>

                      {/* 계좌 MDD 서킷브레이커 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs">
                          {uiLanguage === 'ko' ? '계좌 MDD 서킷브레이커 (%)' : 'Account MDD Breaker (%)'}
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            min="1"
                            max="90"
                            value={autoTradeMddBreaker}
                            onChange={(e) => setAutoTradeMddBreaker(Math.max(1, Math.min(90, parseFloat(e.target.value) || 15)))}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-8 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/30 text-xs">%</span>
                        </div>
                        <p className="text-[10px] text-white/40">
                          {uiLanguage === 'ko' ? '계좌 손실이 설정치에 도달하면 모든 스마트 주문이 자동 중단됩니다.' : 'All Smart Orders halt automatically when account drawdown reaches this threshold.'}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* 타겟 거래 시장 선택 */}
                    <div className="flex flex-col gap-2">
                      <label className="text-white font-bold text-xs flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-cyan-400" />
                        {uiLanguage === 'ko' ? '타겟 거래 시장 (다중 선택)' : 'Target Trading Markets'}
                      </label>
                      <div className="flex items-center gap-2 pt-1">
                        {[
                          { code: 'KR' as const, label: '🇰🇷 한국 (KRX)' },
                          { code: 'US' as const, label: '🇺🇸 미국 (US)' }
                        ].map(m => {
                          const isChecked = targetMarkets.includes(m.code);
                          return (
                            <button
                              key={m.code}
                              type="button"
                              onClick={() => {
                                if (isChecked) {
                                  if (targetMarkets.length > 1) {
                                    setTargetMarkets(targetMarkets.filter(x => x !== m.code));
                                  }
                                } else {
                                  setTargetMarkets([...targetMarkets, m.code]);
                                }
                              }}
                              className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${
                                isChecked
                                  ? 'bg-cyan-500/20 border-cyan-500/40 text-cyan-300 shadow-sm'
                                  : 'bg-black/40 border-white/10 text-white/40 hover:text-white/70'
                              }`}
                            >
                              {m.label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-white/40 leading-relaxed">
                        {uiLanguage === 'ko' ? '스마트 주문이 진입할 거래 시장을 선택합니다. 최소 1개 이상이어야 합니다.' : 'Select the markets Smart Orders may enter. At least one must remain selected.'}
                      </p>
                    </div>

                    {/* 해외주식 통합증거금 적용 여부 */}
                    <div className="flex flex-col gap-2">
                      <label className="text-white font-bold text-xs flex items-center justify-between">
                        <span className="flex items-center gap-1.5">
                          <Coins className="w-3.5 h-3.5 text-cyan-400" />
                          {uiLanguage === 'ko' ? '해외주식 통합증거금 (자동환전 매수)' : 'Cross-Currency Margin'}
                        </span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold transition-colors ${
                          useIntegratedMargin
                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            : 'bg-white/10 text-white/40 border border-white/10'
                        }`}>
                          {useIntegratedMargin
                            ? (uiLanguage === 'ko' ? '적용 중 (ON)' : 'ENABLED')
                            : (uiLanguage === 'ko' ? '미적용 (OFF)' : 'DISABLED')}
                        </span>
                      </label>
                      <div className="flex items-center justify-between bg-black/50 border border-white/10 rounded-xl px-4 py-2.5">
                        <div className="flex flex-col pr-2">
                          <span className="text-xs text-white font-medium">
                            {uiLanguage === 'ko' ? '원화(KRW) 증거금 자동환전 매수 허용' : 'Allow Auto-FX from KRW'}
                          </span>
                          <span className="text-[10px] text-white/50">
                            {useIntegratedMargin
                              ? (uiLanguage === 'ko' ? '달러 부족 시 가용 원화로 자동환전 매수' : 'Orders execute using KRW collateral when USD short')
                              : (uiLanguage === 'ko' ? '보유 달러(USD) 한도 내에서만 매수 집행' : 'Strict execution within available USD balance only')}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setUseIntegratedMargin(!useIntegratedMargin)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors outline-none flex-shrink-0 ${
                            useIntegratedMargin ? 'bg-cyan-500' : 'bg-white/10'
                          }`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            useIntegratedMargin ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </div>
                      <p className="text-[10px] text-white/40 leading-relaxed">
                        {uiLanguage === 'ko'
                          ? '비활성화 시 달러(USD) 보유 잔액 내에서만 해외주식이 매수되며 원화 자동환전을 사용하지 않습니다.'
                          : 'When disabled, overseas stocks will only be purchased within existing USD balance without auto currency exchange.'}
                      </p>
                    </div>
                  </div>
                  {/* 저장 버튼 */}
                  <div className="flex justify-end pt-4 border-t border-white/5">
                    <button
                      onClick={handleSaveAutoTrade}
                      disabled={isSavingTradingConfig}
                      className="px-8 py-3 bg-white text-black hover:bg-cyan-500 hover:text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all"
                    >
                      {isSavingTradingConfig ? (uiLanguage === 'ko' ? '저장 중...' : 'Saving...') : (uiLanguage === 'ko' ? '스마트 주문 설정 저장' : 'Save Smart Order')}
                    </button>
                  </div>
                </div>
              ) : (
              <div className="space-y-6 transition-all duration-300">
                {/* [블록 1] 📡 실시간 시그널 웹훅 알림 (Pro Webhook) */}
                <div className="p-5 rounded-2xl bg-white/[0.03] border border-cyan-500/30 space-y-4">
                  <div className="flex items-center justify-between border-b border-white/10 pb-3">
                    <div className="flex items-center gap-2">
                      <Bell className="w-4 h-4 text-cyan-400" />
                      <h4 className="text-sm font-bold text-white">
                        {uiLanguage === 'ko' ? '1. 실시간 시그널 웹훅 알림' : '1. Real-time Signal Webhook Alert'}
                      </h4>
                    </div>
                      <span className="text-[10px] text-cyan-300 font-semibold px-2 py-0.5 rounded bg-cyan-500/10 border border-cyan-500/20">
                        Pro Webhook
                      </span>
                    </div>

                    {/* 채널 선택 버튼 그룹 */}
                    <div className="flex flex-col gap-2">
                      <label className="text-white/80 font-semibold text-xs">
                        {uiLanguage === 'ko' ? '수신 채널 선택' : 'Target Platform'}
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { id: 'DISCORD' as const, label: 'Discord', desc: '디스코드 웹훅' },
                          { id: 'SLACK' as const, label: 'Slack', desc: '슬랙 인커밍 웹훅' },
                          { id: 'TELEGRAM' as const, label: 'Telegram', desc: '텔레그램 봇 알림' },
                        ].map(ch => (
                          <button
                            key={ch.id}
                            type="button"
                            onClick={() => setAlertChannelType(ch.id)}
                            className={`p-3 rounded-xl border text-left transition-all ${
                              alertChannelType === ch.id
                                ? 'bg-cyan-500/20 border-cyan-500/50 text-cyan-200 shadow-lg shadow-cyan-500/10'
                                : 'bg-black/40 border-white/10 text-white/50 hover:border-white/20 hover:text-white'
                            }`}
                          >
                            <div className="font-bold text-xs">{ch.label}</div>
                            <div className="text-[9px] text-white/40 mt-0.5">{ch.desc}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 채널별 입력 폼 */}
                    {alertChannelType !== 'TELEGRAM' ? (
                      <div className="flex flex-col gap-2">
                        <label className="text-white/80 font-semibold text-xs flex items-center justify-between">
                          <span>{alertChannelType === 'DISCORD' ? 'Discord Webhook URL' : 'Slack Incoming Webhook URL'}</span>
                          <span className="text-[10px] text-white/40 font-mono">HTTPS URL</span>
                        </label>
                        <input
                          type="url"
                          placeholder={
                            alertChannelType === 'DISCORD'
                              ? 'https://discord.com/api/webhooks/...'
                              : 'https://hooks.slack.com/services/...'
                          }
                          value={webhookUrl}
                          onChange={(e) => setWebhookUrl(e.target.value)}
                          className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 focus:border-cyan-500 outline-none font-mono text-white text-xs"
                        />
                        <p className="text-[10px] text-white/40">
                          {alertChannelType === 'DISCORD'
                            ? (uiLanguage === 'ko' ? '디스코드 채널 설정 > 연동 > 웹훅 만들기에서 발급받은 URL을 붙여넣으세요.' : 'Paste webhook URL from Discord Channel Settings > Integrations.')
                            : (uiLanguage === 'ko' ? '슬랙 앱 관리 > Incoming Webhooks에서 발급받은 URL을 붙여넣으세요.' : 'Paste webhook URL from Slack App Directory > Incoming Webhooks.')}
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <label className="text-white/80 font-semibold text-xs flex items-center justify-between">
                          <span>{uiLanguage === 'ko' ? '텔레그램 수신 Chat ID' : 'Telegram Chat ID'}</span>
                          <span className="text-[10px] text-white/40 font-mono">Numeric / Username</span>
                        </label>
                        <input
                          type="text"
                          placeholder={uiLanguage === 'ko' ? '예: 123456789 (숫자 ID 또는 채널 ID)' : 'e.g., 123456789'}
                          value={telegramChatId}
                          onChange={(e) => setTelegramChatId(e.target.value)}
                          className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 focus:border-cyan-500 outline-none font-mono text-white text-xs"
                        />
                        <p className="text-[10px] text-white/40">
                          {uiLanguage === 'ko'
                            ? '텔레그램에서 @userinfobot 또는 @myidbot에 대화를 걸어 확인한 본인의 Chat ID를 입력하세요.'
                            : 'Message @userinfobot on Telegram to retrieve your Chat ID.'}
                        </p>
                      </div>
                    )}

                    {/* 테스트 웹훅 발송 버튼 */}
                    <div className="flex items-center justify-between pt-2 border-t border-white/5">
                      <div className="text-[11px]">
                        {webhookTestMessage && (
                          <span className={webhookTestMessage.isError ? 'text-rose-400' : 'text-emerald-400 font-medium'}>
                            {webhookTestMessage.text}
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleTestWebhookClick}
                        disabled={isTestingWebhook}
                        className="px-4 py-2 bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 disabled:opacity-50"
                      >
                        {isTestingWebhook ? (
                          <>
                            <span className="animate-spin text-sm">⏳</span>
                            <span>{uiLanguage === 'ko' ? '전송 중...' : 'Sending...'}</span>
                          </>
                        ) : (
                          <>
                            <span>🔔</span>
                            <span>{uiLanguage === 'ko' ? '웹훅 테스트 발송' : 'Send Test Webhook'}</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* 웹훅 시그널 피드 발송 기준 안내 */}
                    <div className="p-3 bg-black/40 rounded-xl border border-white/5 space-y-1 text-[10px] text-white/60">
                      <div className="font-semibold text-white/80 flex items-center gap-1">
                        <span>📡 실시간 피드 발송 규격:</span>
                      </div>
                      <ul className="list-disc list-inside space-y-0.5 text-white/50">
                        <li>AI 퀀트 및 매크로 종합 신뢰도 점수가 80점 이상인 주요 시그널만 선별 발송</li>
                        <li>종목명, 티커, 시그널 강도, 매크로 레짐 상태, 목표 비중 정보 포함 (JSON 규격)</li>
                        <li>주문 대행 및 일임 매매를 포함하지 않는 순수 데이터 분석 웹훅 피드입니다.</li>
                      </ul>
                    </div>
                  </div>

                {/* [블록 2] ⚡️ 알고리즘 모의 체결 시뮬레이션 엔진 (Paper Trading Engine) */}
                <div className="space-y-6">
                  {/* 시그널 레이더 체결 방식 선택 (VIRTUAL_SANDBOX vs BROKER_PAPER) */}
                  <div className="p-5 rounded-2xl bg-white/[0.03] border border-cyan-500/30 space-y-4">
                    <div className="flex items-center justify-between border-b border-white/10 pb-3">
                      <div className="flex items-center gap-2">
                        <ZapIcon className="w-4 h-4 text-cyan-400" />
                        <h4 className="text-sm font-bold text-white">
                          {uiLanguage === 'ko' ? '2. 알고리즘 모의 체결 시뮬레이션 엔진' : '2. Algorithmic Paper Trading Engine'}
                        </h4>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${
                        paperExecutionMode === 'BROKER_PAPER'
                          ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20'
                          : 'bg-cyan-500/10 text-cyan-300 border-cyan-500/20'
                      }`}>
                        {paperExecutionMode === 'BROKER_PAPER'
                          ? (uiLanguage === 'ko' ? '증권사 모의투자 (VTS)' : 'Broker VTS')
                          : (uiLanguage === 'ko' ? '클라우드 모의 시드머니' : 'Virtual Sandbox')}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* 1) 순수 가상 시드머니 시뮬레이션 */}
                      <button
                        type="button"
                        onClick={() => setPaperExecutionMode('VIRTUAL_SANDBOX')}
                        className={`p-4 rounded-xl border text-left transition-all relative ${
                          paperExecutionMode === 'VIRTUAL_SANDBOX'
                            ? 'bg-cyan-500/15 border-cyan-500/60 text-white shadow-lg shadow-cyan-500/10 ring-1 ring-cyan-500/30'
                            : 'bg-white/[0.02] border-white/10 text-white/70 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">🧪</span>
                            <span className="font-bold text-xs text-white">
                              {uiLanguage === 'ko' ? '클라우드 모의 시드머니 샌드박스' : 'Virtual Sandbox Simulation'}
                            </span>
                          </div>
                          {paperExecutionMode === 'VIRTUAL_SANDBOX' && (
                            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                          )}
                        </div>
                        <p className="text-[11px] text-white/50 leading-relaxed">
                          {uiLanguage === 'ko'
                            ? '모의 시드머니 기준으로 포지션 비중을 산출하며, 외부 계좌 연동 없이 슬리피지(0.05%) 모델 기반 모의 시뮬레이션을 수행합니다.'
                            : 'Computes positions with virtual seed money, without external broker APIs, using a 0.05% slippage model.'}
                        </p>
                      </button>

                      {/* 2) 증권사 모의투자(VTS) OpenAPI 연동 */}
                      <button
                        type="button"
                        onClick={() => setPaperExecutionMode('BROKER_PAPER')}
                        className={`p-4 rounded-xl border text-left transition-all relative ${
                          paperExecutionMode === 'BROKER_PAPER'
                            ? 'bg-emerald-500/15 border-emerald-500/60 text-white shadow-lg shadow-emerald-500/10 ring-1 ring-emerald-500/30'
                            : 'bg-white/[0.02] border-white/10 text-white/70 hover:border-white/20'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-emerald-400" />
                            <span className="font-bold text-xs text-white">
                              {uiLanguage === 'ko' ? '증권사 모의투자 계좌 연동 (VTS)' : 'Brokerage Paper Trading (VTS)'}
                            </span>
                          </div>
                          {paperExecutionMode === 'BROKER_PAPER' && (
                            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                          )}
                        </div>
                        <p className="text-[11px] text-white/50 leading-relaxed">
                          {uiLanguage === 'ko'
                            ? '국내 증권사(한국투자증권·LS증권·키움증권) 모의투자(VTS) OpenAPI를 연동하여 모의 시뮬레이션을 실행합니다. (실거래/출금 불가)'
                            : 'Connect domestic broker Paper Trading (VTS) OpenAPI to simulate strategy executions. (No live trading/withdrawal)'}
                        </p>
                      </button>
                    </div>

                    {/* 증권사 모의투자 선택 시 브로커 지정 및 키 등록 안내 */}
                    {paperExecutionMode === 'BROKER_PAPER' && (
                      <div className="p-4 rounded-xl bg-black/40 border border-emerald-500/30 space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <label className="text-xs font-bold text-white flex items-center gap-1.5">
                            <Building2 className="w-4 h-4 text-emerald-400" />
                            <span>{uiLanguage === 'ko' ? '모의투자(VTS) 증권사 선택' : 'Paper Trading Broker'}</span>
                          </label>
                          <select
                            value={paperBroker}
                            onChange={(e) => setPaperBroker(e.target.value as any)}
                            className="px-3 py-1.5 rounded-lg bg-slate-900 border border-white/15 text-xs text-white focus:outline-none focus:border-emerald-500"
                          >
                            <option value="smart_routing" className="bg-slate-900 text-white">
                              {uiLanguage === 'ko' ? '🧠 스마트 라우팅 (자동 분배)' : '🧠 Smart Routing'}
                            </option>
                            <option value="kis" className="bg-slate-900 text-white">
                              {uiLanguage === 'ko' ? '한국투자증권 · 모의투자 (VTS)' : 'Korea Investment (VTS)'}
                            </option>
                            <option value="ls" className="bg-slate-900 text-white">
                              {uiLanguage === 'ko' ? 'LS증권 · 모의투자' : 'LS Securities (Mock)'}
                            </option>
                            <option value="kiwoom" className="bg-slate-900 text-white">
                              {uiLanguage === 'ko' ? '키움증권 · 모의투자' : 'Kiwoom Securities (Mock)'}
                            </option>
                          </select>
                        </div>
                        <div className="p-2.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[11px] text-emerald-300/90 leading-relaxed flex items-start gap-2">
                          <span className="text-emerald-400 shrink-0 mt-0.5">ℹ️</span>
                          <span>
                            {uiLanguage === 'ko'
                              ? '증권사 모의투자(VTS) 전용 시뮬레이션입니다. 모의 계좌 API 키 등록은 [마이페이지 > 모의투자 증권사 및 계좌 연동]에서 설정하실 수 있습니다. (실거래/주문대행 불가)'
                              : 'Brokerage simulation via Paper Trading (VTS) OpenAPI. Register VTS keys in [My Page > Paper Trading Brokerage Sync]. (No live trading/order agency)'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* 모의 시뮬레이션 시드머니 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs flex items-center justify-between">
                          <span>
                            {uiLanguage === 'ko' ? `모의 샌드박스 시드머니 (${autoTradeCurrency})` : `Virtual Seed Capital (${autoTradeCurrency})`}
                          </span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${activeMarketMode === 'KR' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                            {activeMarketMode === 'KR' ? '🇰🇷 한국 증시 기준' : '🇺🇸 미국 증시 기준'}
                          </span>
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            value={activeMarketMode === 'KR' ? virtualSeedKRW : virtualSeedUSD}
                            onChange={(e) => {
                              const val = Math.max(0, parseFloat(e.target.value) || 0);
                              if (activeMarketMode === 'KR') {
                                setVirtualSeedKRW(val);
                              } else {
                                setVirtualSeedUSD(val);
                              }
                            }}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-14 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/40 text-xs font-semibold">{autoTradeCurrency}</span>
                        </div>
                        <p className="text-[10px] text-white/40">
                          {uiLanguage === 'ko' ? '모의 시뮬레이션에서 자산 비중 배분 및 수량 산출의 기준이 되는 모의 자본금입니다.' : 'Base virtual capital used for calculating simulated position allocations.'}
                        </p>
                      </div>

                      {/* 일일 누적 시뮬레이션 한도 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs flex items-center justify-between">
                          <span>{uiLanguage === 'ko' ? `1일 최대 모의 시뮬레이션 한도 (${autoTradeCurrency})` : `Daily Max Simulation Limit (${autoTradeCurrency})`}</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${activeMarketMode === 'KR' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-blue-500/20 text-blue-400 border border-blue-500/30'}`}>
                            {activeMarketMode === 'KR' ? '🇰🇷 한국 증시' : '🇺🇸 미국 증시'}
                          </span>
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            value={activeMarketMode === 'KR' ? paperDailyOrderLimitKR : paperDailyOrderLimitUS}
                            onChange={(e) => {
                              const val = Math.max(0, parseFloat(e.target.value) || 0);
                              if (activeMarketMode === 'KR') {
                                setPaperDailyOrderLimitKR(val);
                              } else {
                                setPaperDailyOrderLimitUS(val);
                              }
                            }}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-14 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/40 text-xs font-semibold">{autoTradeCurrency}</span>
                        </div>
                      </div>

                      {/* 종목별 최대 편입 비중 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs">
                          {uiLanguage === 'ko' ? '종목별 최대 편입 비중 (%)' : 'Max Weight per Stock (%)'}
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            min="1"
                            max="100"
                            value={paperMaxWeight}
                            onChange={(e) => setPaperMaxWeight(Math.max(1, Math.min(100, parseFloat(e.target.value) || 25)))}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-8 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/30 text-xs">%</span>
                        </div>
                      </div>

                      {/* 종목 하방 방어 기준선 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs">
                          {uiLanguage === 'ko' ? '시뮬레이션 하방 방어 기준선 (%)' : 'Simulation Drawdown Defense (%)'}
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={paperStopLoss}
                            onChange={(e) => setPaperStopLoss(Math.max(1, Math.min(50, parseFloat(e.target.value) || 8)))}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-8 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/30 text-xs">%</span>
                        </div>
                      </div>

                      {/* 샌드박스 MDD 서킷브레이커 */}
                      <div className="flex flex-col gap-2">
                        <label className="text-white font-bold text-xs">
                          {uiLanguage === 'ko' ? '모의 포트폴리오 MDD 서킷브레이커 (%)' : 'Sandbox Portfolio MDD Breaker (%)'}
                        </label>
                        <div className="relative flex items-center">
                          <input
                            type="number"
                            min="1"
                            max="90"
                            value={paperMddBreaker}
                            onChange={(e) => setPaperMddBreaker(Math.max(1, Math.min(90, parseFloat(e.target.value) || 15)))}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 pr-8 focus:border-cyan-500 outline-none font-mono text-white text-sm"
                          />
                          <span className="absolute right-4 text-white/30 text-xs">%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                {/* 저장 버튼 */}
                <div className="flex justify-end pt-4 border-t border-white/5">
                  <button
                    onClick={handleSaveAutoTrade}
                    disabled={isSavingTradingConfig}
                    className="px-8 py-3 bg-white text-black hover:bg-cyan-500 hover:text-white rounded-xl text-xs font-black uppercase tracking-widest transition-all"
                  >
                    {isSavingTradingConfig ? (uiLanguage === 'ko' ? '저장 중...' : 'Saving...') : (uiLanguage === 'ko' ? '설정 저장' : 'Save Config')}
                  </button>
                </div>
              </div>
              )
            ) : (
              <div className="text-center py-8 text-white/40 text-sm">
                {isLiveMode
                  ? (uiLanguage === 'ko' ? '스마트 주문 설정이 비활성화되어 있습니다. 우측 상단의 스위치를 켜서 설정을 진행하세요.' : 'Smart Order settings are disabled. Toggle the switch above to configure.')
                  : (uiLanguage === 'ko' ? '시그널 레이더 설정이 비활성화되어 있습니다. 우측 상단의 스위치를 켜서 설정을 진행하세요.' : 'Signal Radar settings are disabled. Toggle the switch above to configure.')}
              </div>
            )}
          </CollapsibleSection>
        );
      })()}

      {/* 6. AI Personalization & Models (MMO-010) */}
      {(() => {
        const personalizationProduct = activeAddons.find(a => a.id === 'AI_PERSONALIZATION_ADDON');
        const isPersonalizationActive = activeAddons.length === 0 || !!personalizationProduct;
        const hasPersonalizationAccessDynamic = checkAddonAccess('AI_PERSONALIZATION_ADDON');
        const personalizationMinTier: MembershipTier = (personalizationProduct?.minTier as MembershipTier) || 'TIER5';
        const isPersonalizationTierSufficient = canAccessTier(normalizedUserTier as any, personalizationMinTier);

        let currentTierFreeAddOns: string[] = [];
        if (productsConfig && productsConfig[normalizedUserTier]) {
          currentTierFreeAddOns = productsConfig[normalizedUserTier].freeAddOns || [];
        } else {
          if (normalizedUserTier === 'TIER5') {
            currentTierFreeAddOns = ['CHAT_PREDICTION_ADDON', 'API_ACCESS_ADDON', 'AI_PERSONALIZATION_ADDON', 'OPTIONS_ANALYTICS_ADDON'];
          } else if (normalizedUserTier === 'TIER4') {
            currentTierFreeAddOns = ['AI_PERSONALIZATION_ADDON'];
          }
        }
        const isPersonalizationFree = currentTierFreeAddOns.includes('AI_PERSONALIZATION_ADDON') && !isTierExpired;

        if (!isPersonalizationActive || !hasPersonalizationAccessDynamic) return null;
        // HIDE 모드에서 비활성화된 경우 섹션 전체 숨김
        if (!isAiPersonalizationEnabled && featureDisabledMode === 'HIDE') return null;

        return (
          <div className="bg-gradient-to-br from-purple-950/20 via-black/40 to-indigo-950/20 border border-purple-500/20 p-8 rounded-3xl space-y-6 relative overflow-hidden text-left">
            {!isAiPersonalizationEnabled && (
              <div className="absolute inset-0 z-20 flex flex-col items-center justify-center p-6 bg-black/80 backdrop-blur-sm transition-all text-center">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500/20 to-red-500/10 border border-rose-500/30 flex items-center justify-center text-xl mb-3">
                  ⚠️
                </div>
                <h3 className="font-bold text-lg mb-1 text-rose-400">
                  {uiLanguage === 'ko' ? 'AI 개인화 설정 기능 비활성화' : 'AI Personalization Disabled'}
                </h3>
                <p className="text-xs text-white/50 max-w-md">
                  {uiLanguage === 'ko' 
                    ? '현재 시스템 점검으로 인해 AI 모델 및 프롬프트 개인화 설정이 관리자에 의해 전역 비활성화되었습니다.' 
                    : 'AI personalization settings are temporarily disabled globally by the administrator.'}
                </p>
              </div>
            )}

            <div className={`space-y-6 ${!isAiPersonalizationEnabled ? 'filter blur-[1.5px] pointer-events-none' : ''}`}>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Cpu className="w-5 h-5 text-emerald-400" />
                  {uiLanguage === 'ko' ? 'AI 개인화 및 모델 설정' : 'AI Personalization & Models'}
                </h2>
                <div className="flex items-center gap-3">
                  {hasPersonalizationAccessDynamic && isPersonalizationFree && (
                    <span className="px-3 py-1 bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 text-xs font-bold rounded-lg select-none">
                      {uiLanguage === 'ko' ? '멤버십 혜택' : 'Membership Benefit'}
                    </span>
                  )}
                  <button
                    onClick={handleToggleAiPersonalization}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-black ${aiPersonalizationEnabled ? 'bg-emerald-500' : 'bg-white/10'}`}
                  >
                    <span className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${aiPersonalizationEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
                  </button>
                </div>
              </div>
              <p className="text-white/40 text-sm">
                {uiLanguage === 'ko'
                  ? '분석 단계별 AI 모델, 프롬프트, API 키를 사용자 정의하여 나만의 분석 엔진을 구축합니다.'
                  : 'Customize AI models, prompts, and API keys for each analysis stage to build your own engine.'}
              </p>

              {aiPersonalizationEnabled && (
                <div className="space-y-6 pt-4 border-t border-white/5">
                  {/* User LLM API Keys */}
                  <div className="space-y-4">
                    <h3 className="text-xs font-black uppercase text-white/40 tracking-wider">
                      {uiLanguage === 'ko' ? '사용자 API 키 설정 (Optional)' : 'User API Keys (Optional)'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {['gemini', 'openai', 'anthropic'].map(provider => {
                        const keyEntry = userApiKeys.find((k: any) => k.provider === provider);
                        const isVerified = verifiedProviders.includes(provider) || (keyEntry && keyEntry.api_key);
                        const isDefault = provider === 'gemini';
                        return (
                          <div key={provider} className={`bg-white/5 p-4 rounded-xl border space-y-3 ${isVerified ? 'border-emerald-500/20' : 'border-white/5'}`}>
                            <div className="flex items-center justify-between">
                              <span className={`text-[10px] font-black uppercase tracking-widest ${isVerified ? PROVIDER_COLORS[provider] : 'text-white/30'}`}>{provider}</span>
                              {isDefault && <span className="text-[9px] bg-blue-500/10 text-blue-400 border border-blue-500/20 px-1.5 py-0.5 rounded-full font-bold">Default</span>}
                            </div>
                            <input
                              type="password"
                              placeholder={uiLanguage === 'ko' ? `${provider} API 키 입력` : `Enter ${provider} API Key`}
                              defaultValue={keyEntry ? '••••••••' : ''}
                              onBlur={(e) => {
                                if (e.target.value && e.target.value !== '••••••••') {
                                  handleUpdateApiKey(provider, e.target.value);
                                }
                              }}
                              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-emerald-500 font-mono"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Stage-wise Model Selector */}
                  <div className="space-y-4 pt-4 border-t border-white/5">
                    <h3 className="text-xs font-black uppercase text-white/40 tracking-wider">
                      {uiLanguage === 'ko' ? '분석 태스크별 실행 엔진 모델 설정' : 'Stage-wise Model Configuration'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { key: 'macro_analysis', label: uiLanguage === 'ko' ? '거시 경제 퀀트 분석 엔진' : 'Macro Quant Engine' },
                        { key: 'portfolio_optimization', label: uiLanguage === 'ko' ? '포트폴리오 비중 시뮬레이션 엔진' : 'Portfolio Optimization Engine' },
                        { key: 'stock_analysis', label: uiLanguage === 'ko' ? '종합 기술적 종목 분석 엔진' : 'Stock Technical Analyzer' },
                        { key: 'chat_prediction', label: uiLanguage === 'ko' ? (isLiveMode ? '스마트 주문 & 시황 챗 플러스 AI' : '시황 분석 & 시뮬레이션 챗 플러스 AI') : (isLiveMode ? 'Smart Order & Quant Bot' : 'Quant Simulation Bot') },
                      ].map((stage) => {
                        const config = userAiConfigs.find(c => c.category === stage.key) || { model_id: 'gemini-2.5-flash' };
                        return (
                          <div key={stage.key} className="p-4 bg-white/5 border border-white/5 rounded-xl space-y-3">
                            <span className="text-xs font-bold text-white/80 block">{stage.label}</span>
                            <ModelSelect
                              value={config.model_id}
                              availableProviders={verifiedProviders}
                              onChange={(modelId) => handleUpdateAiConfig(stage.key, { model_id: modelId })}
                              label={uiLanguage === 'ko' ? '모델 선택' : 'Select Model'}
                              canUseAllModels={limits.canUseAllModels}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* 7. Device Security & Session Management */}
      <CollapsibleSection
        id="section-device-mgmt"
        variant="card"
        title={uiLanguage === 'ko' ? '접속 디바이스 관리 및 보안 이력' : 'Device Security & Active Sessions'}
        subtitle={uiLanguage === 'ko' ? '현재 로그인된 기기 리스트를 확인하고 원격 세션을 관리합니다.' : 'Manage logged-in devices and remote active sessions.'}
        icon={<Lock className="w-5 h-5 text-emerald-400" />}
        isOpen={!collapsedSections['device_mgmt']}
        onToggle={() => toggleSection('device_mgmt')}
        contentClassName="space-y-6"
      >
        <DeviceManagement userProfile={userProfile} />
      </CollapsibleSection>

      {/* 9. Service Information Section */}
      <div className="bg-white/5 border border-white/10 p-8 rounded-3xl space-y-6 text-left">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Info className="w-5 h-5 text-cyan-400" />
          {uiLanguage === 'ko' ? '서비스 정보' : 'Service Information'}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="flex flex-col gap-2 p-5 bg-gradient-to-br from-cyan-500/5 to-transparent border border-cyan-500/10 rounded-2xl">
            <div className="flex items-center gap-2 text-[10px] font-bold text-white/30 uppercase tracking-widest">
              <Tag className="w-3.5 h-3.5" />
              {uiLanguage === 'ko' ? '제품 버전' : 'Product Version'}
            </div>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-2xl font-black text-white tracking-tight">
                v{formatProductVersion(typeof __APP_PRODUCT_VERSION__ !== 'undefined' ? __APP_PRODUCT_VERSION__ : '1.0')}
              </span>
              <span className="text-[11px] font-mono text-cyan-400/90 bg-cyan-500/10 px-2 py-0.5 rounded-md border border-cyan-500/20 font-semibold">
                Build {typeof __APP_BUILD_VERSION__ !== 'undefined' ? __APP_BUILD_VERSION__ : packageJson.version}
              </span>
            </div>
            <div className="text-[10px] text-cyan-400/70 font-bold">EZOptix Official Release</div>
          </div>

          <div className="flex flex-col gap-2 p-5 bg-gradient-to-br from-emerald-500/5 to-transparent border border-emerald-500/10 rounded-2xl">
            <div className="flex items-center gap-2 text-[10px] font-bold text-white/30 uppercase tracking-widest">
              <Server className="w-3.5 h-3.5" />
              {uiLanguage === 'ko' ? '배포 환경' : 'Environment'}
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-base font-black text-white uppercase">Production</span>
            </div>
            <div className="text-[10px] text-white/30">Cloudflare Pages</div>
          </div>

          <div className="flex flex-col gap-2 p-5 bg-gradient-to-br from-purple-500/5 to-transparent border border-purple-500/10 rounded-2xl">
            <div className="flex items-center gap-2 text-[10px] font-bold text-white/30 uppercase tracking-widest">
              <Info className="w-3.5 h-3.5" />
              {uiLanguage === 'ko' ? '최종 업데이트' : 'Last Updated'}
            </div>
            <div className="text-base font-black text-white">
              {__BUILD_DATE__}
            </div>
            <div className="text-[10px] text-white/30">KST (Asia/Seoul)</div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <span className="text-[11px] text-white/20">
            {uiLanguage === 'ko' ? '자세한 변경 이력은 릴리즈 노트를 참고하세요.' : 'See release notes for full changelog.'}
          </span>
          <a
            href="https://github.com/lyrico220/MarketMacroOptix/releases"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-[11px] font-bold text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Release Notes
          </a>
        </div>
      </div>

      <AnimatePresence>
        {cancelModalConfig && (
          <SubscriptionCancelModal
            tier={cancelModalConfig.tier}
            subId={cancelModalConfig.subId}
            uiLanguage={uiLanguage}
            onClose={() => setCancelModalConfig(null)}
            onCancelled={async () => {
              setCancelModalConfig(null);
              if (onRefreshProfile) await onRefreshProfile();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
